import React, { useState, useEffect, useMemo, useCallback, useRef, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { 
  Star, 
  ChevronRight, 
  LineChart, 
  BarChart2, 
  Calendar,
  Clock,
  Zap,
  Target,
  Share2,
  BookOpen,
  CheckCircle2,
  Globe,
  Shield,
  Users,
  Check,
  X,
  Loader2,
  Bell,
  Rocket,
  Play,
  Crown,
  Headphones,
  Tag,
  MessageSquare,
  User,
  LogOut,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../utils/gtag';
import { useTheme } from '../contexts/ThemeContext';
import { Toast } from '../components/Toast';
import { AccountStatsCards } from '../components/dashboard/AccountStatsCards';
import { createCheckoutSession, checkSubscriptionStatus, getCustomerPortalSession } from '../services/stripe';
import { doc, getDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore';""
import { db } from '../lib/firebase';
import { registerAcquisitionParams } from '../lib/posthog';
import { ImageComparisonSlider } from '../components/ImageComparisonSlider';
import { BacktestDemoChart } from '../components/BacktestDemoChart';
import { Navbar } from '../components/Navbar';
import { LandingSeoHead } from '../components/LandingSeoHead';
import { Footer } from '../components/Footer';
import { PricingPlanCards } from '../components/pricing/PricingPlanCards';
import { PricingPremiumOfferCard } from '../components/pricing/PricingPremiumOfferCard';
import { getPricingCtaState, STRIPE_BILLING_PORTAL, buildPremiumStripeCheckoutUrl } from '../utils/pricingCtaLogic';
import { affiliateService } from '../services/affiliateService';
import { saveAffiliateCookie } from '../utils/affiliateCookies';
import { getLocalPropFirmLogo } from '../utils/propfirm/localLogos';

// Images WebP (version optimisée)
import logoSvgWebP from '../assets/webp-landing/focuspips.webp';
import excelLogoPng from '../assets/png-landing/excel.svg.webp';
import notionLogoPng from '../assets/png-landing/notion.png';
import edgewonkLogoPng from '../assets/png-landing/edgewonk.png';
// atlas-main.png replaced by webp-landing/atlas-main.webp (see atlasMainWebP below)
import avisTrustpilot1Png from '../assets/webp-landing/avis-trustpilot-1.webp';
import avisTrustpilot3Png from '../assets/webp-landing/avis-trustpilot-3.webp';
import avisTrustpilot2Png from '../assets/webp-landing/avis-trustpilot-2.webp';
import avisTrustpilot4Png from '../assets/webp-landing/avis-trustpilot-4.webp';
import avisTrustpilot5Png from '../assets/webp-landing/avis-trustpilot-5.webp';
import avisTrustpilot6Png from '../assets/webp-landing/avis-trustpilot-6.webp';
import partenaireImg1Png from '../assets/webp-landing/partenaire-img-1.webp';
import partenaireImg2Png from '../assets/webp-landing/partenaire-img-2.webp';
import partenaireImg3Png from '../assets/webp-landing/partenaire-img-3.webp';
import partenaireImg1WebP from '../assets/webp-landing/partenaire-img-1.webp';
import partenaireImg2WebP from '../assets/webp-landing/partenaire-img-2.webp';
import partenaireImg3WebP from '../assets/webp-landing/partenaire-img-3.webp';
import dashboardDemoPng from '../assets/webp-landing/dashboard-demo-section.webp';
import dashboardTransfoPng from '../assets/webp-landing/dashboard-demo-transformation.webp';
import journalTransfoPng from '../assets/webp-landing/journal-demo-transformation.webp';
import plansTransfoPng from '../assets/webp-landing/plans-demo-transformation.webp';
import IAImageWebP from '../assets/webp-landing/IA-Principale.webp';
import beforeImageWebP from '../assets/webp-landing/before-light.webp';
import dashboardMainImageWebP from '../assets/webp-landing/dashboard-main-light.webp';
import dashboardAfterBlackWebP from '../assets/webp-landing/dashboard-after-black.webp';
import trustpilotLogoWebP from '../assets/webp-landing/trustpilot.webp';
import laurelLeftWebP from '../assets/webp-landing/laurel-left.webp';
import laurelRightWebP from '../assets/webp-landing/laurel-right.webp';
import hugoFxLogoWebP from '../assets/webp-landing/hugofx.webp';
import avis1WebP from '../assets/webp-landing/avis-1.webp';
import avis2WebP from '../assets/webp-landing/avis-2.webp';
import avis3WebP from '../assets/webp-landing/avis-3.webp';
import avis4WebP from '../assets/webp-landing/avis-4.webp';
import avis5WebP from '../assets/webp-landing/avis-5.webp';
import avis6WebP from '../assets/webp-landing/avis-6.webp';
import journalDemoImageWebP from '../assets/webp-landing/journal-demo.webp';
import backtestDemoImageWebP from '../assets/webp-landing/backtest-demo-dark.webp';
import notesDemoImageWebP from '../assets/webp-landing/notes-demo.webp';
import calendarDemoImageWebP from '../assets/webp-landing/calendar-demo.webp';
import calculatriceDemoImageWebP from '../assets/webp-landing/calculatrice-demo.webp';
import saionalityDemoImageWebP from '../assets/webp-landing/saisonality-demo.webp';
import retailsDemoImageWebP from '../assets/webp-landing/retails-demo.webp';
import tagsDemoImageWebP from '../assets/webp-landing/tags-demo.webp';
import plansDemoImageWebP from '../assets/webp-landing/plans-demo.webp';
import iaDemoImageWebP from '../assets/webp-landing/ia-demo.webp';
import plansTradingImageWebP from '../assets/webp-landing/all-plans-trading.webp';
import tagAnalyseIaImageWebP from '../assets/webp-landing/tag-analyse-ia.webp';
import analyseAiFullImageWebP from '../assets/webp-landing/analyse-ai-full-light.webp';
import analyseIaChartImageWebP from '../assets/webp-landing/analyse-ai-charts.webp';
import cTraderLogoWebP from '../assets/webp-landing/cTrader.webp';
import journalDemoDarkWebP from '../assets/webp-landing/journal-demo-dark.webp';
import notesDemoDarkWebP from '../assets/webp-landing/notes-demo-dark.webp';
import calendarDemoDarkWebP from '../assets/webp-landing/calendar-demo-dark.webp';
import calculatriceDemoDarkWebP from '../assets/webp-landing/calculatrice-demo-dark.webp';
import saionalityDemoDarkWebP from '../assets/webp-landing/saisonality-demo-dark.webp';
import retailsDemoDarkWebP from '../assets/webp-landing/retails-demo-dark.webp';
import tagsDemoDarkWebP from '../assets/webp-landing/tags-demo-dark.webp';
import plansDemoDarkWebP from '../assets/webp-landing/plans-demo-dark.webp';
import iaDemoDarkWebP from '../assets/webp-landing/ia-demo-dark.webp';
import dashboardTransfoWebP from '../assets/webp-landing/dashboard-demo-transformation.webp';
import journalTransfoWebP from '../assets/webp-landing/journal-demo-transformation.webp';
import plansTransfoWebP from '../assets/webp-landing/plans-demo-transformation.webp';
import dashboardTransfoSmWebP from '../assets/webp-landing/dashboard-demo-transformation-sm.webp';
import journalTransfoSmWebP from '../assets/webp-landing/journal-demo-transformation-sm.webp';
import plansTransfoSmWebP from '../assets/webp-landing/plans-demo-transformation-sm.webp';
import dashboardAfterBlackSmWebP from '../assets/webp-landing/dashboard-after-black-sm.webp';
import partenaireImg3SmWebP from '../assets/webp-landing/partenaire-img-3-sm.webp';
import atlasMainWebP from '../assets/webp-landing/atlas-main.webp';
import metatrader5LogoWebP from '../assets/webp-landing/metatrader5-logo.webp';
import metatrader4LogoWebP from '../assets/webp-landing/metatrader4-logo.webp';
import ninjaTraderLogoWebP from '../assets/webp-landing/ninjatrader-logo.webp';
import tradelockerLogoWebP from '../assets/webp-landing/tradelocker.webp';
import tradingviewLogoWebP from '../assets/webp-landing/tradingview-logo-main.webp';
import tradovateLogoWebP from '../assets/webp-landing/tradovate.webp';

// Images PNG (fallback pour anciens navigateurs)
import logoSvg from '../assets/webp-landing/focuspips.webp';
import IAImage from '../assets/webp-landing/IA-Principale.webp'; 
import beforeImage from '../assets/webp-landing/before-light.webp';
import dashboardMainImage from '../assets/webp-landing/dashboard-main-light.webp';
import dashboardMainOldImage from '../assets/webp-landing/before-light.webp';
import dashboardAfterBlack from '../assets/webp-landing/dashboard-after-black.webp';
import trustpilotLogo from '../assets/png-landing/trustpilot.svg';
import propfirmReglesWebP from '../assets/webp-landing/propfirm-regles.webp';
import propfirmReglesPng from '../assets/png-landing/propfirm-regles.png';
import propfirmSimulateurWebP from '../assets/webp-landing/propfirm-simulateur.webp';
import propfirmSimulateurPng from '../assets/png-landing/propfirm-simulateur.png';
import propfirmChallengesWebP from '../assets/webp-landing/propfirm-challenges.webp';
import propfirmChallengesPng from '../assets/png-landing/propfirm-challenges.png';
import laurelLeft from '../assets/webp-landing/laurel-left.webp';
import laurelRight from '../assets/webp-landing/laurel-right.webp';
import hugoFxLogo from '../assets/webp-landing/hugofx.webp';
import avis1 from '../assets/webp-landing/avis-1.webp';
import avis2 from '../assets/webp-landing/avis-2.webp';
import avis3 from '../assets/webp-landing/avis-3.webp';
import avis4 from '../assets/webp-landing/avis-4.webp';
import avis5 from '../assets/webp-landing/avis-5.webp';
import avis6 from '../assets/webp-landing/avis-6.webp';
import journalDemoImage from '../assets/webp-landing/journal-demo-dark.webp';
import backtestDemoImage from '../assets/webp-landing/backtest-demo-dark.webp';
import notesDemoImage from '../assets/webp-landing/notes-demo-dark.webp';
import calendarDemoImage from '../assets/webp-landing/calendar-demo-dark.webp';
import calculatriceDemoImage from '../assets/webp-landing/calculatrice-demo-dark.webp';
import saionalityDemoImage from '../assets/webp-landing/saisonality-demo-dark.webp';
import retailsDemoImage from '../assets/webp-landing/retails-demo-dark.webp';
import tagsDemoImage from '../assets/webp-landing/tags-demo-dark.webp';
import plansDemoImage from '../assets/webp-landing/plans-demo-dark.webp';
import iaDemoImage from '../assets/webp-landing/ia-demo-dark.webp';
import plansTradingImage from '../assets/webp-landing/all-plans-trading.webp';
import tagAnalyseIaImage from '../assets/webp-landing/tag-analyse-ia.webp';
import analyseAiFullImage from '../assets/webp-landing/analyse-ai-full-light.webp';
import analyseIaChartImage from '../assets/webp-landing/analyse-ai-charts.webp';
import ninjaTraderLogo from '../assets/webp-landing/ninjatrader-logo.webp';
import metatrader5Logo from '../assets/webp-landing/metatrader5-logo.webp';
import metatrader4Logo from '../assets/webp-landing/metatrader4-logo.webp';
import tradelockerLogo from '../assets/webp-landing/tradelocker.webp';
import tradingviewLogo from '../assets/webp-landing/tradingview-logo-main.webp';
import cTraderLogo from '../assets/webp-landing/cTrader.webp';
import tradovateLogo from '../assets/webp-landing/tradovate.webp';

// Autres ressources
import mainFeatures from '../assets/main-features.svg'; 
import replayDemoVideo from '../assets/replay-demo.mp4';

// Placeholder images
const PLACEHOLDER_IMAGES = {
  dashboard: 'https://placehold.co/1200x800/1e1b4b/ffffff?text=Trading+Dashboard',
  feature1: 'https://placehold.co/600x400/312e81/ffffff?text=Trading+Journal',
  feature2: 'https://placehold.co/600x400/312e81/ffffff?text=Real-time+Analysis',
  feature3: 'https://placehold.co/600x400/312e81/ffffff?text=Performance+Tracking',
  stats: 'https://placehold.co/1200x600/1e1b4b/ffffff?text=Trading+Stats',
  insight2: 'https://placehold.co/600x400/312e81/ffffff?text=Pattern+Recognition',
  insight3: 'https://placehold.co/600x400/312e81/ffffff?text=Market+Analysis',
  backtest: 'https://placehold.co/1200x600/1e1b4b/ffffff?text=Backtest+Dashboard',
  community: 'https://placehold.co/1200x600/1e1b4b/ffffff?text=Trading+Community',
  mobile: 'https://placehold.co/400x800/1e1b4b/ffffff?text=Mobile+App',
  integrations: 'https://placehold.co/1200x600/1e1b4b/ffffff?text=Platform+Integrations',
  iaSecondary: 'https://placehold.co/600x800/312e81/ffffff?text=AI+Secondary'
};

const features = [
  {
    icon: Calendar,
    title: "Journal de Trading",
    description: "Enregistrez et suivez tous vos trades avec précision"
  },
  {
    icon: Clock,
    title: "Analyse en Temps Réel",
    description: "Obtenez des insights instantanés sur vos performances"
  },
  {
    icon: Zap,
    title: "Suivi de Performance",
    description: "Surveillez votre progression avec des métriques avancées"
  },
  {
    icon: Target,
    title: "Gestion des Risques",
    description: "Gardez vos risques de trading sous contrôle"
  },
  {
    icon: Share2,
    title: "Partage de Trades",
    description: "Partagez vos trades avec votre communauté"
  },
  {
    icon: BookOpen,
    title: "Ressources d'Apprentissage",
    description: "Accédez à du contenu éducatif et des guides"
  }
];

const PRICING_CONFIG = {
  products: {
    basic: {
      features: [
        'Compte De Trading',
        'Tableau De Bord Analytique Complet',
        'Synchronisation Auto Avec Metatrader 4/5 + CTrader',
        'Intégration Et Analyse De L\'IA',
        'Captures D\'Écrans Illimitées',
        'Accès À Tous Les Outils D\'Analyses',
        'Tags Personnalisés'
      ]
    },
    premium: {
      priceId: 'price_1QYpZ1DTjOptRNbVD4VFN11K',
      monthlyPrice: 19.99,
      yearlyPrice: 199.99,
      features: [
        'Comptes De Trading Illimités',
        'Tableau De Bord Analytique Complet',
        'Synchronisation Auto Avec Metatrader 4/5 + CTrader',
        'Intégration Et Analyse De L\'IA',
        'Captures D\'Écrans Illimitées',
        'Accès À Tous Les Outils D\'Analyses',
        'Tags Personnalisés'
      ]
    }
  }
};

// Composant optimisé avec memo
const FeatureCard = React.memo(({ icon: Icon, title, description }: { icon: React.ComponentType<any>; title: string; description: string }) => (
  <div className="feature-card p-6 rounded-lg bg-gray-50">
    <Icon className="w-8 h-8 mb-4 text-indigo-500" />
    <h3 className="text-xl font-semibold mb-2 text-gray-900">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
));

// Version ultra-optimisée pour le chargement des images avec WebP et fallback
const FastImage = React.memo(({ src, srcWebP, alt, className, style, priority = false }: { src: string; srcWebP?: string; alt: string; className: string; style?: React.CSSProperties; priority?: boolean }) => { 
  if (srcWebP) {
    return (
      <picture>
        <source srcSet={srcWebP} type="image/webp" />
        <img
          src={src}
          alt={alt}
          className={className}
          style={{
            ...style,
            display: 'block',
          }}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
        />
      </picture>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={{
        ...style,
        display: 'block',
      }}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
    />
  );
});

// Composant pour l'effet 3D hover avec support WebP
const Image3D = React.memo(({ src, srcWebP, alt, className, style, priority = false, width, height }: { src: string; srcWebP?: string; alt: string; className: string; style?: React.CSSProperties; priority?: boolean; width?: number; height?: number }) => {
  return (
    <div 
      className="relative group perspective-1000"
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const rotateX = ((y - centerY) / centerY) * -7;
        const rotateY = ((x - centerX) / centerX) * 7;
        
        const img = e.currentTarget.querySelector('img');
        if (img) {
          img.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
        }
      }}
      onMouseLeave={(e) => {
        const img = e.currentTarget.querySelector('img');
        if (img) {
          img.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
        }
      }}
    >
      {srcWebP ? (
        <picture>
          <source srcSet={srcWebP} type="image/webp" />
          <img
            src={src}
            alt={alt}
            className={className}
            style={{
              ...style,
              transition: 'transform 0.3s ease-out'
            }}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            width={width}
            height={height}
          />
        </picture>
      ) : (
        <img
          src={src}
          alt={alt}
          className={className}
          style={{
            ...style,
            transition: 'transform 0.3s ease-out'
          }}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          width={width}
          height={height}
        />
      )}
    </div>
  );
});

const AnimatedText = React.memo(({ 
  children,
  delay = 0,
  duration = 520
}: { 
  children: React.ReactNode;
  delay?: number;
  duration?: number;
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Intersection Observer pour déclencher l'animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setTimeout(() => {
            setIsVisible(true);
          }, delay);
        }
      },
      {
        threshold: 0.08,
        rootMargin: '0px 0px 18% 0px'
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [isVisible, delay]);

  return (
    <div 
      ref={ref}
      style={{
        willChange: 'transform, opacity',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
      }}
    >
      <div
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0px)' : 'translateY(24px)',
          transition: `all ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
          transitionDelay: isVisible ? `${delay}ms` : '0ms',
          willChange: 'transform, opacity',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
        }}
      >
        {children}
      </div>
    </div>
  );
});

// ── Marquee des prop firms ────────────────────────────────────────────────────
// Les logos viennent des assets déjà bundlés du cockpit (`src/assets/logos/`) :
// aucune image en double, et une firm dont le logo change en jeu de données est
// à jour ici aussi. La clé est le nom de dossier, pas l'id de registre — les
// deux divergent pour quelques firms (`the-5-ers`, `my-funded-futures`…).
const PROP_FIRM_MARQUEE_ROWS: Array<Array<{ slug: string; name: string }>> = [
  [
    { slug: 'ftmo', name: 'FTMO' },
    { slug: 'fundednext', name: 'FundedNext' },
    { slug: 'the-5-ers', name: 'The5ers' },
    { slug: 'topstep', name: 'Topstep' },
    { slug: 'apex-trader-funding', name: 'Apex' },
    { slug: 'funding-pips', name: 'Funding Pips' },
    { slug: 'alpha-capital-group', name: 'Alpha Capital' },
    { slug: 'e8-markets', name: 'E8 Markets' },
  ],
  [
    { slug: 'my-funded-futures', name: 'MyFundedFutures' },
    { slug: 'take-profit-trader', name: 'Take Profit Trader' },
    { slug: 'fxify', name: 'FXIFY' },
    { slug: 'goat-funded-trader', name: 'Goat Funded' },
    { slug: 'blueberry-funded', name: 'Blueberry' },
    { slug: 'maven-trading', name: 'Maven Trading' },
    { slug: 'breakout', name: 'Breakout' },
    { slug: 'hola-prime', name: 'Hola Prime' },
  ],
  [
    { slug: 'funded-trading-plus', name: 'Funded Trading Plus' },
    { slug: 'city-traders-imperium', name: 'City Traders Imperium' },
    { slug: 'instant-funding', name: 'Instant Funding' },
    { slug: 'blue-guardian', name: 'Blue Guardian' },
    { slug: 'finotive-funding', name: 'Finotive' },
    { slug: 'tradeday', name: 'TradeDay' },
    { slug: 'tradeify', name: 'Tradeify' },
    { slug: 'earn2trade', name: 'Earn2Trade' },
  ],
];

const PropFirmMarquee = React.memo(() => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
    {PROP_FIRM_MARQUEE_ROWS.map((row, rowIndex) => (
      <div
        key={rowIndex}
        style={{
          width: 'calc(100vw - 32px)', maxWidth: '820px', margin: '0 auto', overflow: 'hidden',
          maskImage: 'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
        }}
      >
        <div
          style={{
            display: 'flex', gap: '36px', width: 'max-content',
            // Sens et vitesse différents à chaque rangée : trois marquees
            // synchronisés se liraient comme un seul bloc qui glisse.
            animation: `pfMarquee ${[62, 54, 70][rowIndex]}s linear infinite${rowIndex === 1 ? ' reverse' : ''}`,
          }}
        >
          {[...row, ...row].map((firm, i) => {
            const logo = getLocalPropFirmLogo(firm.slug);
            return (
              <div key={`${firm.slug}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                {logo && <img src={logo} alt="" loading="lazy" style={{ width: 34, height: 34, objectFit: 'contain', borderRadius: 8, flexShrink: 0 }} />}
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.86rem', fontWeight: 600 }}>{firm.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    ))}
  </div>
));

// ── Cockpit animé ─────────────────────────────────────────────────────────────

/** Format monétaire du cockpit : espace fine insécable comme dans l'app. */
const pfMoney = (value: number, locale = 'fr-FR') => `${Math.round(value).toLocaleString(locale).replace(/ | /g, ' ')} $`;

/** Compte de 0 à `value` quand la carte entre à l'écran. */
function usePfCountUp(value: number, active: boolean, duration = 1100) {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (!active) return;
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      setCurrent(value * (1 - Math.pow(1 - progress, 3)));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    // `requestAnimationFrame` ne tourne pas dans un onglet en arrière-plan : sans
    // ce garde-fou, le montant resterait affiché à 0 $ — pire qu'une absence
    // d'animation. Les timers, eux, continuent de s'exécuter.
    const guard = setTimeout(() => setCurrent(value), duration + 500);
    return () => { cancelAnimationFrame(frame); clearTimeout(guard); };
  }, [value, active, duration]);
  return current;
}

/**
 * Réplique du cockpit `/propfirm` (onglet Live), reconstruite en JSX plutôt que
 * capturée : les jauges se remplissent à l'entrée à l'écran et le compte à
 * rebours tourne réellement. Une capture figée ne rendrait pas ce qui fait la
 * valeur de cet écran — le fait qu'il soit vivant.
 *
 * Deux profils tournent en boucle infinie (crossfade doux) : un challenge
 * classique où le trader peut encore trader (les deux jauges ne sont volontairement
 * PAS au même niveau, pour montrer que le cockpit sait laquelle bloque en premier),
 * et un compte Click Funded où la perte journalière est atteinte — le cockpit
 * l'interdit de trader avant même que la casse soit faite.
 */
/**
 * Tableau comparatif "Pourquoi pas juste Excel".
 *
 * Rendu en 4 colonnes indépendantes (libellés + 3 concurrents), chacune étant
 * sa propre carte : c'est ce qui permet à la colonne FocusPips d'être un bloc
 * continu (bordure dégradée + halo) sans être coupée à chaque ligne. L'alignement
 * horizontal des lignes est garanti par une hauteur de cellule fixe et partagée
 * (CMP_ROW_H), pas par une grille — une grille CSS remettrait le découpage.
 *
 * Valeurs : 'check' → ✓ · 'x' → ✗ · toute autre string → texte affiché tel quel.
 */
const CMP_ROW_H = 'h-[60px] sm:h-[68px]';
const CMP_HEAD_H = 'h-[84px] sm:h-[96px]';
/**
 * `row` → clé sous `landing.compare.rows`. Les colonnes valent 'check', 'x', ou une
 * clé sous `landing.compare.values` : le libellé traduit est résolu au rendu.
 */
const CMP_ROWS = [
  { row: 'sync',        excel: 'x',                edgewonk: 'csv_only',        focus: 'check' },
  { row: 'coach',       excel: 'x',                edgewonk: 'x',               focus: 'atlas_included' },
  { row: 'backtest',    excel: 'x',                edgewonk: 'stats_simulator', focus: 'check' },
  { row: 'replay',      excel: 'x',                edgewonk: 'x',               focus: 'check' },
  { row: 'metrics',     excel: 'manual_formulas',  edgewonk: 'partial',         focus: 'metrics_auto' },
  { row: 'tags',        excel: 'x',                edgewonk: 'check',           focus: 'check' },
  { row: 'calendar',    excel: 'x',                edgewonk: 'x',               focus: 'check' },
  { row: 'seasonality', excel: 'x',                edgewonk: 'x',               focus: 'check' },
  { row: 'retail',      excel: 'x',                edgewonk: 'x',               focus: 'check' },
  { row: 'calculator',  excel: 'x',                edgewonk: 'x',               focus: 'check' },
];

/** Une cellule de valeur du tableau comparatif. `highlight` = colonne FocusPips. */
const CmpCell = ({ value, highlight = false, last = false }: { value: string; highlight?: boolean; last?: boolean }) => {
  const { t } = useTranslation();
  return (
  <div
    className={`${CMP_ROW_H} flex items-center justify-center px-2 ${last ? '' : 'border-b'}`}
    style={{ borderColor: highlight ? 'rgba(167,139,250,0.14)' : 'rgba(255,255,255,0.06)' }}
  >
    {value === 'check' ? (
      <Check className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: highlight ? '#c084fc' : '#9b87f5' }} />
    ) : value === 'x' ? (
      <X className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: 'rgba(255,255,255,0.35)' }} />
    ) : (
      <span
        className="text-center text-[10px] leading-tight sm:text-sm font-medium"
        style={{ color: highlight ? '#c084fc' : 'rgba(255,255,255,0.5)' }}
      >
        {t(`landing.compare.values.${value}`)}
      </span>
    )}
  </div>
  );
};

const PROP_FIRM_PROFILES = [
  {
    firmSlug: 'ftmo',
    label: 'FTMO Swing 100 000 $',
    pill: 'Phase 1',
    dailyLimit: 5_000, dailyUsed: 1_967,
    totalLimit: 10_000, totalUsed: 7_661, totalFloor: 90_000,
    verdict: 'go' as const,
    blockedBy: 'total' as const,
  },
  {
    firmSlug: 'clickfunded',
    label: 'Click Funded 50 000 $',
    pill: 'Phase 2',
    dailyLimit: 2_500, dailyUsed: 2_250,
    totalLimit: 5_000, totalUsed: 3_800, totalFloor: 46_200,
    verdict: 'no-go' as const,
    blockedBy: 'daily' as const,
  },
];

const PropFirmCockpitMock = React.memo(() => {
  const { t, i18n } = useTranslation();
  const money = useCallback((value: number) => pfMoney(value, i18n.language), [i18n.language]);
  const [visible, setVisible] = useState(false);
  const [profileIndex, setProfileIndex] = useState(0);
  const [faded, setFaded] = useState(false);
  const [secondsToReset, setSecondsToReset] = useState(6 * 3600 + 41 * 60 + 12);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.35 },
    );
    observer.observe(element);
    // Filet : cette carte porte tout le contenu de la démo. Si l'observer ne se
    // déclenche jamais (onglet ouvert en arrière-plan, navigateur exotique),
    // elle resterait à opacity 0 — donc invisible, sans erreur nulle part.
    const fallback = setTimeout(() => setVisible(true), 2500);
    return () => { observer.disconnect(); clearTimeout(fallback); };
  }, []);

  useEffect(() => {
    if (!visible) return;
    const timer = setInterval(() => setSecondsToReset((s) => (s <= 0 ? 24 * 3600 : s - 1)), 1000);
    return () => clearInterval(timer);
  }, [visible]);

  // Boucle infinie entre les profils : fondu sortant, on change les données
  // pendant que la carte est invisible, puis fondu entrant. Le rythme lent
  // (7s d'affichage, 0.7s de transition) garde ça posé plutôt que clignotant.
  useEffect(() => {
    if (!visible) return;
    const cycle = setInterval(() => {
      setFaded(true);
      setTimeout(() => {
        setProfileIndex((i) => (i + 1) % PROP_FIRM_PROFILES.length);
        setFaded(false);
      }, 700);
    }, 7000);
    return () => clearInterval(cycle);
  }, [visible]);

  const profile = PROP_FIRM_PROFILES[profileIndex];
  const dailyRemaining = Math.max(0, profile.dailyLimit - profile.dailyUsed);
  const totalRemaining = Math.max(0, profile.totalLimit - profile.totalUsed);
  const dailyRatio = Math.min(1, profile.dailyUsed / profile.dailyLimit);
  const totalRatio = Math.min(1, profile.totalUsed / profile.totalLimit);

  const dailyCount = usePfCountUp(dailyRemaining, visible);
  const totalCount = usePfCountUp(totalRemaining, visible);

  const pad = (n: number) => String(n).padStart(2, '0');
  const countdown = `${pad(Math.floor(secondsToReset / 3600))}:${pad(Math.floor((secondsToReset % 3600) / 60))}:${pad(secondsToReset % 60)}`;
  const firmLogo = getLocalPropFirmLogo(profile.firmSlug);

  const gauges = [
    {
      key: 'daily', title: t('landing.propfirm.cockpit.daily_title'), remaining: dailyCount, ratio: dailyRatio, limit: profile.dailyLimit,
      color: dailyRatio >= 0.85 ? '#f87171' : dailyRatio >= 0.6 ? '#f59e0b' : '#10b981', caption: t('landing.propfirm.cockpit.daily_caption'),
      status: dailyRatio >= 0.85 ? t('landing.propfirm.cockpit.status_almost') : dailyRatio >= 0.6 ? t('landing.propfirm.cockpit.status_watch') : t('landing.propfirm.cockpit.status_ok'),
    },
    {
      key: 'total', title: t('landing.propfirm.cockpit.total_title'), remaining: totalCount, ratio: totalRatio, limit: profile.totalLimit,
      color: totalRatio >= 1 ? '#f87171' : totalRatio >= 0.6 ? '#f59e0b' : '#10b981', caption: t('landing.propfirm.cockpit.total_caption', { amount: money(profile.totalFloor) }),
      status: totalRatio >= 1 ? t('landing.propfirm.cockpit.status_reached') : totalRatio >= 0.6 ? t('landing.propfirm.cockpit.status_watch') : t('landing.propfirm.cockpit.status_ok'),
    },
  ];

  const isGo = profile.verdict === 'go';
  const blockedGauge = gauges.find((g) => g.key === profile.blockedBy)!;

  return (
    <div
      ref={ref}
      style={{
        maxWidth: '860px', margin: '0 auto', borderRadius: '20px', padding: 'clamp(18px, 4vw, 28px)',
        background: 'linear-gradient(160deg, rgba(167,139,250,0.07) 0%, rgba(11,11,12,0.6) 55%), #0A0A0D',
        boxShadow: '0 30px 90px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.07)',
        opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: 'opacity 0.8s ease, transform 0.8s ease',
      }}
    >
      {/* Contenu dynamique — fondu doux à chaque changement de profil */}
      <div
        style={{
          opacity: faded ? 0 : 1,
          transform: faded ? 'translateY(6px) scale(0.994)' : 'translateY(0) scale(1)',
          transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        {/* En-tête du compte suivi */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {firmLogo && <img src={firmLogo} alt="" loading="lazy" style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 10, flexShrink: 0 }} />}
          <span style={{ color: '#fff', fontSize: '0.92rem', fontWeight: 700 }}>{profile.label}</span>
          <span style={{ padding: '2px 9px', borderRadius: '100px', background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', fontWeight: 600 }}>{profile.pill}</span>
          <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.35)', fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
            {t('landing.propfirm.cockpit.reset_in')} <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>{countdown}</span>
          </span>
        </div>

        {/* Verdict — bloc identique à PropFirmVerdictBanner sur /propfirm, onglet
            Live : même icône, même structure, état "go" ou "no-go" selon le
            profil affiché. Fond/bordure alignés sur les cartes de jauges
            ci-dessous — le tint coloré n'existe dans l'app que dans le composant
            source, la CSS globale (.bento-card) l'écrase en prod. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', borderRadius: '14px', padding: '16px', background: 'rgba(255,255,255,0.025)', marginBottom: '16px' }}>
          <span style={{ flexShrink: 0, color: isGo ? '#34d399' : '#f87171', width: 'clamp(28px, 5vw, 32px)', height: 'clamp(28px, 5vw, 32px)' }}>
            {isGo ? (
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ width: '100%', height: '100%' }}>
                <path d="M15.06 10.5a.75.75 0 0 0-1.12-1l-3.011 3.374l-.87-.974a.75.75 0 0 0-1.118 1l1.428 1.6a.75.75 0 0 0 1.119 0z" />
                <path fillRule="evenodd" clipRule="evenodd" d="M12 1.25c-.937 0-1.833.307-3.277.801l-.727.25c-1.481.506-2.625.898-3.443 1.23c-.412.167-.767.33-1.052.495c-.275.16-.55.359-.737.626c-.185.263-.281.587-.341.9c-.063.324-.1.713-.125 1.16c-.048.886-.048 2.102-.048 3.678v1.601c0 6.101 4.608 9.026 7.348 10.224l.027.011c.34.149.66.288 1.027.382c.387.1.799.142 1.348.142c.55 0 .96-.042 1.348-.142c.367-.094.687-.233 1.026-.382l.028-.011c2.74-1.198 7.348-4.123 7.348-10.224V10.39c0-1.576 0-2.792-.048-3.679a9 9 0 0 0-.125-1.16c-.06-.312-.156-.636-.34-.9c-.188-.266-.463-.465-.738-.625a9 9 0 0 0-1.052-.495c-.818-.332-1.962-.724-3.443-1.23l-.727-.25c-1.444-.494-2.34-.801-3.277-.801M9.08 3.514c1.615-.552 2.262-.764 2.92-.764s1.305.212 2.92.764l.572.196c1.513.518 2.616.896 3.39 1.21c.387.158.667.29.864.404q.144.084.208.139c.038.03.053.048.055.05a.4.4 0 0 1 .032.074q.03.082.063.248a7 7 0 0 1 .1.958c.046.841.046 2.015.046 3.624v1.574c0 5.176-3.87 7.723-6.449 8.849c-.371.162-.586.254-.825.315c-.228.059-.506.095-.976.095s-.748-.036-.976-.095c-.24-.06-.454-.153-.825-.315c-2.58-1.126-6.449-3.674-6.449-8.849v-1.574c0-1.609 0-2.783.046-3.624a7 7 0 0 1 .1-.958q.032-.166.063-.248c.018-.05.03-.07.032-.074a.4.4 0 0 1 .055-.05q.064-.055.208-.14c.197-.114.477-.245.864-.402c.774-.315 1.877-.693 3.39-1.21z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ width: '100%', height: '100%' }}>
                <path d="M10.0303 8.96965C9.73744 8.67676 9.26256 8.67676 8.96967 8.96965C8.67678 9.26254 8.67678 9.73742 8.96967 10.0303L10.9394 12L8.96969 13.9697C8.6768 14.2625 8.6768 14.7374 8.96969 15.0303C9.26258 15.3232 9.73746 15.3232 10.0304 15.0303L12 13.0607L13.9696 15.0303C14.2625 15.3232 14.7374 15.3232 15.0303 15.0303C15.3232 14.7374 15.3232 14.2625 15.0303 13.9696L13.0607 12L15.0303 10.0303C15.3232 9.73744 15.3232 9.26256 15.0303 8.96967C14.7374 8.67678 14.2626 8.67678 13.9697 8.96967L12 10.9393L10.0303 8.96965Z" />
                <path fillRule="evenodd" clipRule="evenodd" d="M12 1.25C11.0625 1.25 10.1673 1.55658 8.72339 2.05112L7.99595 2.30014C6.51462 2.8072 5.3714 3.19852 4.55303 3.53099C4.14078 3.69846 3.78637 3.86067 3.50098 4.02641C3.22634 4.1859 2.95082 4.38484 2.76363 4.65154C2.5786 4.91516 2.48293 5.23924 2.42281 5.55122C2.36031 5.87556 2.32262 6.26464 2.2983 6.71136C2.25 7.59836 2.25 8.81351 2.25 10.3896V11.9914C2.25 18.0924 6.85803 21.0175 9.59833 22.2146L9.62543 22.2264C9.96523 22.3749 10.2846 22.5144 10.6516 22.6084C11.0391 22.7076 11.4507 22.75 12 22.75C12.5493 22.75 12.9609 22.7076 13.3484 22.6084C13.7154 22.5144 14.0348 22.3749 14.3745 22.2264L14.4017 22.2146C17.142 21.0175 21.75 18.0924 21.75 11.9914V10.3898C21.75 8.81361 21.75 7.5984 21.7017 6.71136C21.6774 6.26464 21.6397 5.87556 21.5772 5.55122C21.5171 5.23924 21.4214 4.91516 21.2364 4.65154C21.0492 4.38484 20.7737 4.1859 20.499 4.02641C20.2136 3.86067 19.8592 3.69846 19.447 3.53099C18.6286 3.19852 17.4854 2.8072 16.004 2.30013L15.2766 2.05112C13.8327 1.55658 12.9375 1.25 12 1.25ZM9.08062 3.5143C10.6951 2.96164 11.3423 2.75 12 2.75C12.6577 2.75 13.3049 2.96164 14.9194 3.5143L15.4922 3.71037C17.0048 4.22814 18.1079 4.60605 18.8824 4.92069C19.269 5.07774 19.5491 5.20935 19.7457 5.32353C19.8428 5.3799 19.9097 5.42642 19.9543 5.46273C19.9922 5.49349 20.0066 5.51092 20.0087 5.51348C20.0106 5.5166 20.0231 5.53737 20.0406 5.58654C20.0606 5.64265 20.0827 5.72309 20.1043 5.83506C20.148 6.06169 20.1811 6.37301 20.2039 6.79292C20.2497 7.63411 20.25 8.80833 20.25 10.4167V11.9914C20.25 17.1665 16.3801 19.7135 13.8012 20.84C13.4297 21.0023 13.2152 21.0941 12.9764 21.1552C12.7483 21.2136 12.47 21.25 12 21.25C11.53 21.25 11.2517 21.2136 11.0236 21.1552C10.7848 21.0941 10.5703 21.0023 10.1988 20.84C7.6199 19.7135 3.75 17.1665 3.75 11.9914V10.4167C3.75 8.80833 3.75028 7.63411 3.79608 6.79292C3.81894 6.37301 3.85204 6.06169 3.89571 5.83506C3.91729 5.72309 3.93944 5.64265 3.95943 5.58654C3.97693 5.5374 3.98936 5.51663 3.99129 5.51349C3.99336 5.51095 4.0078 5.49351 4.04567 5.46273C4.09034 5.42642 4.15722 5.3799 4.25429 5.32353C4.4509 5.20935 4.731 5.07774 5.11759 4.92069C5.8921 4.60605 6.99521 4.22814 8.5078 3.71037L9.08062 3.5143Z" />
              </svg>
            )}
          </span>
          <div style={{ minWidth: 0 }}>
            <p style={{ color: isGo ? '#34d399' : '#f87171', fontSize: 'clamp(1.125rem, 3vw, 1.5rem)', fontWeight: 700, lineHeight: 1.2, margin: 0 }}>
              {isGo ? t('landing.propfirm.cockpit.verdict_go') : t('landing.propfirm.cockpit.verdict_nogo')}
            </p>
            <p style={{ color: '#a0a0a0', fontSize: 'clamp(0.875rem, 2vw, 1rem)', margin: '4px 0 0', lineHeight: 1.4 }}>
              {isGo
                ? t('landing.propfirm.cockpit.verdict_go_detail', { amount: money(totalRemaining) })
                : t('landing.propfirm.cockpit.verdict_nogo_detail', { amount: money(dailyRemaining) })}
            </p>
          </div>
        </div>

        {/* Les deux jauges */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {gauges.map((gauge, index) => (
            <div key={gauge.key} style={{ borderRadius: '14px', padding: '16px', background: 'rgba(255,255,255,0.025)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '10px' }}>
                <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem', fontWeight: 600 }}>{gauge.title}</span>
                <span style={{ padding: '2px 8px', borderRadius: '100px', border: `1px solid ${gauge.color}44`, color: gauge.color, fontSize: '0.64rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{gauge.status}</span>
              </div>
              <p style={{ color: gauge.color, fontSize: 'clamp(1.5rem, 4vw, 1.9rem)', fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 2px', fontVariantNumeric: 'tabular-nums' }}>
                {money(gauge.remaining)}
              </p>
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.74rem', margin: '0 0 12px' }}>{gauge.caption}</p>
              <div style={{ height: 6, borderRadius: '100px', background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: '100px', background: gauge.color,
                  width: visible && !faded ? `${gauge.ratio * 100}%` : '0%',
                  transition: `width 1.2s cubic-bezier(0.22,1,0.36,1) ${index * 0.15 + 0.2}s`,
                }} />
              </div>
              <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.7rem', margin: '8px 0 0' }}>
                {t('landing.propfirm.cockpit.consumed', { percent: Math.round(gauge.ratio * 100), amount: money(gauge.limit) })}
              </p>
            </div>
          ))}
        </div>

        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', margin: '16px 0 0', textAlign: 'center' }}>
          <Trans
            i18nKey={isGo ? 'landing.propfirm.cockpit.blocked_go' : 'landing.propfirm.cockpit.blocked_nogo'}
            values={{ gauge: blockedGauge.title.toLowerCase() }}
            components={{ 1: <span style={{ color: blockedGauge.color, fontWeight: 600 }} /> }}
          />
        </p>
      </div>
    </div>
  );
});

// Composant pour le scroll infini des avis
const ReviewsInfiniteScroll = React.memo(({ reviews }: { reviews: Array<{ png: string; webp: string }> }) => {
  const [scrollOffset, setScrollOffset] = useState(0);
  const animationRef = useRef<number>();

  // Diviser les avis en 2 colonnes
  const column1 = reviews.filter((_, i) => i % 2 === 0); // avis 0, 2, 4
  const column2 = reviews.filter((_, i) => i % 2 === 1); // avis 1, 3, 5

  useEffect(() => {
    let lastTime = Date.now();
    const scrollSpeed = 30; // pixels par seconde
    
    const animate = () => {
      const now = Date.now();
      const delta = (now - lastTime) / 1000; // temps écoulé en secondes
      lastTime = now;
      
      setScrollOffset(prev => prev + (scrollSpeed * delta));
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div className="absolute inset-0 flex gap-4 px-4">
      {/* Colonne 1 */}
      <div className="flex-1 relative overflow-hidden">
        <div
          style={{
            transform: `translateY(-${scrollOffset % 2000}px)`,
            willChange: 'transform',
          }}
        >
          {/* Premier ensemble */}
          <div className="space-y-4">
            {column1.map((avis, index) => (
              <div 
                key={`col1-first-${index}`}
                className="relative overflow-hidden rounded-2xl shadow-lg"
                style={{
                  background: '#f6f6f6',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <picture>
                  <source srcSet={avis.webp} type="image/webp" />
                  <img 
                    src={avis.png} 
                    alt={`Avis client ${index * 2 + 1}`}
                    className="w-full h-auto"
                  />
                </picture>
              </div>
            ))}
          </div>
          {/* Dupliquer pour scroll infini */}
          <div className="space-y-4 mt-4">
            {column1.map((avis, index) => (
              <div 
                key={`col1-second-${index}`}
                className="relative overflow-hidden rounded-2xl shadow-lg"
                style={{
                  background: '#f6f6f6',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <picture>
                  <source srcSet={avis.webp} type="image/webp" />
                  <img 
                    src={avis.png} 
                    alt={`Avis client ${index * 2 + 1}`}
                    className="w-full h-auto"
                  />
                </picture>
              </div>
            ))}
          </div>
          {/* Tripler pour encore plus de fluidité */}
          <div className="space-y-4 mt-4">
            {column1.map((avis, index) => (
              <div 
                key={`col1-third-${index}`}
                className="relative overflow-hidden rounded-2xl shadow-lg"
                style={{
                  background: '#f6f6f6',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <picture>
                  <source srcSet={avis.webp} type="image/webp" />
                  <img 
                    src={avis.png} 
                    alt={`Avis client ${index * 2 + 1}`}
                    className="w-full h-auto"
                  />
                </picture>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Colonne 2 */}
      <div className="flex-1 relative overflow-hidden">
        <div
          style={{
            transform: `translateY(-${scrollOffset % 2000}px)`,
            willChange: 'transform',
          }}
        >
          {/* Premier ensemble */}
          <div className="space-y-4">
            {column2.map((avis, index) => (
              <div 
                key={`col2-first-${index}`}
                className="relative overflow-hidden rounded-2xl shadow-lg"
                style={{
                  background: '#f6f6f6',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <picture>
                  <source srcSet={avis.webp} type="image/webp" />
                  <img 
                    src={avis.png} 
                    alt={`Avis client ${index * 2 + 2}`}
                    className="w-full h-auto"
                  />
                </picture>
              </div>
            ))}
          </div>
          {/* Dupliquer pour scroll infini */}
          <div className="space-y-4 mt-4">
            {column2.map((avis, index) => (
              <div 
                key={`col2-second-${index}`}
                className="relative overflow-hidden rounded-2xl shadow-lg"
                style={{
                  background: '#f6f6f6',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <picture>
                  <source srcSet={avis.webp} type="image/webp" />
                  <img 
                    src={avis.png} 
                    alt={`Avis client ${index * 2 + 2}`}
                    className="w-full h-auto"
                  />
                </picture>
              </div>
            ))}
          </div>
          {/* Tripler pour encore plus de fluidité */}
          <div className="space-y-4 mt-4">
            {column2.map((avis, index) => (
              <div 
                key={`col2-third-${index}`}
                className="relative overflow-hidden rounded-2xl shadow-lg"
                style={{
                  background: '#f6f6f6',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <picture>
                  <source srcSet={avis.webp} type="image/webp" />
                  <img 
                    src={avis.png} 
                    alt={`Avis client ${index * 2 + 2}`}
                    className="w-full h-auto"
                  />
                </picture>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

// Composant d'animation de chiffres ultra smooth style Apple
const AnimatedCounter = React.memo(({ 
  endValue, 
  duration = 2000, 
  delay = 0,
  className = "",
  style
}: { 
  endValue: number; 
  duration?: number; 
  delay?: number;
  className?: string;
  style?: React.CSSProperties;
}) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number>();
  const startTimeRef = useRef<number>();

  // Fonction d'easing ultra smooth style Apple (easeOutCubic)
  const easeOutCubic = (t: number): number => {
    return 1 - Math.pow(1 - t, 3);
  };

  // Fonction pour formater le nombre avec des points comme séparateurs de milliers
  const formatNumber = (num: number): string => {
    const str = Math.floor(num).toString();
    return str.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  // Animation du compteur
  const animate = useCallback((startTime: number, startValue: number, endVal: number, duration: number) => {
    const animateFrame = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing ultra smooth
      const easedProgress = easeOutCubic(progress);
      const currentValue = startValue + (endVal - startValue) * easedProgress;
      
      setCount(currentValue);
      
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animateFrame);
      } else {
        setCount(endVal); // S'assurer que la valeur finale est exacte
      }
    };
    
    frameRef.current = requestAnimationFrame(animateFrame);
  }, []);

  // Intersection Observer pour déclencher l'animation quand l'élément devient visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.08,
        rootMargin: '0px 0px 18% 0px'
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [isVisible]);

  // Démarrer l'animation quand visible
  useEffect(() => {
    if (isVisible && startTimeRef.current === undefined) {
      const startAnimation = () => {
        startTimeRef.current = performance.now();
        animate(performance.now(), 0, endValue, duration);
      };

      if (delay > 0) {
        const timer = setTimeout(startAnimation, delay);
        return () => clearTimeout(timer);
      } else {
        startAnimation();
      }
    }

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [isVisible, endValue, duration, delay, animate]);

  return (
    <div 
      ref={ref}
      className={`${className} transition-all duration-1000 ${
        isVisible 
          ? 'opacity-100 transform translate-y-0' 
          : 'opacity-0 transform translate-y-8'
      }`}
      style={{
        ...style,
        willChange: 'transform, opacity',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
      }}
    >
      +{formatNumber(count)}
    </div>
  );
});

// Composant pour les blocs de stratégie cliquables
const StrategyBlocks = React.memo(() => {
  const { t } = useTranslation();
  const [activeBlock, setActiveBlock] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [scrollOffset, setScrollOffset] = useState(0);
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();

  const blocks = [
    {
      title: 'Création de plans précis',
      image: plansTradingImage,
      imageWebP: plansTradingImageWebP,
      description: 'Définis et track tes règles de trading pour rester cohérent'
    },
    {
      title: 'Ajout de tags sur chacun des trades',
      image: tagAnalyseIaImage,
      imageWebP: tagAnalyseIaImageWebP,
      description: 'Mesure l\'efficacité de chaque stratégie avec des métriques détaillées'
    },
    {
      title: 'Analyse de performance pour chaque donnée',
      image: analyseAiFullImage,
      imageWebP: analyseAiFullImageWebP,
      description: 'Crée des plans qui évoluent avec tes résultats réels'
    }
  ];

  // Auto-rotation des blocs toutes les 5 secondes avec progress bar
  useEffect(() => {
    if (!isPlaying) {
      setProgress(0);
      return;
    }
    
    setProgress(0); // Reset au début
    let startTime = Date.now();
    const duration = 5000; // 5 secondes
    
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / duration) * 100, 100);
      
      setProgress(newProgress);
      
      if (newProgress >= 100) {
        setActiveBlock((current) => (current + 1) % blocks.length);
      }
    }, 50);

    return () => clearInterval(progressInterval);
  }, [blocks.length, isPlaying, activeBlock]);

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  // Reset progress quand on change de bloc manuellement
  useEffect(() => {
    if (!isPlaying) {
      setProgress(0);
    }
  }, [activeBlock, isPlaying]);

  // Animation continue du scroll pour le bloc "Plan dynamique"
  useEffect(() => {
    let startTime = Date.now();
    const scrollSpeed = 50; // pixels par seconde
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const pixelsMoved = (elapsed / 1000) * scrollSpeed;
      
      // Reset quand on a scrollé la hauteur d'une image
      // On suppose que l'image fait environ 2000px de hauteur
      const imageHeight = 2000;
      setScrollOffset(pixelsMoved % imageHeight);
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div ref={sectionRef} className="w-full px-8 sm:px-12 lg:px-16" style={{ minHeight: '600px' }}>
      <div className="grid grid-cols-1 lg:grid-cols-[35%_65%] gap-4 items-center" style={{ minHeight: '500px' }}>
        {/* Blocs à gauche */}
        <div className="space-y-2">
          {blocks.map((block, index) => (
            <div
              key={index}
              onClick={() => {
                setActiveBlock(index);
                setProgress(0);
              }}
              className={`pr-4 py-4 pl-12 cursor-pointer transition-all duration-300`}
              style={{ 
                borderRadius: activeBlock === index ? '16px' : '48px'
              }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="flex items-center justify-center"
                  style={{
                    width: '32px',
                    height: '32px',
                    minWidth: '32px',
                    minHeight: '32px',
                    flexShrink: 0,
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: activeBlock === index ? '#000000' : '#9ca3af',
                    color: activeBlock === index ? '#000000' : '#9ca3af',
                    fontWeight: '600',
                    fontSize: '16px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {index + 1}
                </div>
                <h3 
                  className="text-xl font-semibold"
                  style={{
                    color: activeBlock === index ? '#000000' : '#9ca3af'
                  }}
                >
                  {block.title}
                </h3>
              </div>
              {activeBlock === index && (
                <p 
                  className="text-gray-600 text-sm mt-3"
                  style={{
                    animation: 'fadeInUp 0.5s ease-out',
                    opacity: 1
                  }}
                >
                  {block.description}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Image à droite */}
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Bloc 0 */}
          <div 
            className="relative"
            style={{
              opacity: activeBlock === 0 ? 1 : 0,
              transform: activeBlock === 0 ? 'scale(1)' : 'scale(0.95)',
              transition: 'opacity 0.7s ease-in-out, transform 0.7s ease-in-out',
              position: activeBlock === 0 ? 'relative' : 'absolute',
              pointerEvents: activeBlock === 0 ? 'auto' : 'none',
              width: '100%'
            }}
          >
            {/* Glow effect derrière l'image */}
            <div 
              className="absolute inset-0 blur-3xl opacity-10 rounded-2xl pointer-events-none"
              style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3), rgba(251, 146, 60, 0.2))',
                transform: 'scale(0.9)'
              }}
            />
            <picture>
              <source srcSet={blocks[0].imageWebP} type="image/webp" />
              <img
                src={blocks[0].image}
                alt={blocks[0].title}
                className="w-full h-auto relative"
                width={2038}
                height={1077}
                style={{ 
                  maxHeight: '500px',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  borderRadius: '16px',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 80px rgba(139, 92, 246, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              />
            </picture>
          </div>
          
          {/* Bloc 1 */}
          <div 
            className="relative"
            style={{
              opacity: activeBlock === 1 ? 1 : 0,
              transform: activeBlock === 1 ? 'scale(1)' : 'scale(0.95)',
              transition: 'opacity 0.7s ease-in-out, transform 0.7s ease-in-out',
              position: activeBlock === 1 ? 'relative' : 'absolute',
              pointerEvents: activeBlock === 1 ? 'auto' : 'none',
              width: '100%'
            }}
          >
            {/* Glow effect derrière l'image */}
            <div 
              className="absolute inset-0 blur-3xl opacity-10 rounded-2xl pointer-events-none"
              style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3), rgba(251, 146, 60, 0.2))',
                transform: 'scale(0.9)'
              }}
            />
            <picture>
              <source srcSet={blocks[1].imageWebP} type="image/webp" />
              <img
                src={blocks[1].image}
                alt={blocks[1].title}
                className="w-full h-auto relative"
                width={1920}
                height={1080}
                style={{ 
                  maxHeight: '500px',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  borderRadius: '16px',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 80px rgba(139, 92, 246, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              />
            </picture>
          </div>
          
          {/* Bloc 2 - Carrousel infini vers le bas pour "Plan dynamique" */}
          <div 
            className="relative"
            style={{
              opacity: activeBlock === 2 ? 1 : 0,
              transform: activeBlock === 2 ? 'scale(1)' : 'scale(0.95)',
              transition: 'opacity 0.7s ease-in-out, transform 0.7s ease-in-out',
              position: activeBlock === 2 ? 'relative' : 'absolute',
              pointerEvents: activeBlock === 2 ? 'auto' : 'none',
              width: '100%'
            }}
          >
            {/* Glow effect derrière le carousel */}
            <div 
              className="absolute inset-0 blur-3xl opacity-10 rounded-2xl pointer-events-none"
              style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3), rgba(251, 146, 60, 0.2))',
                transform: 'scale(0.9)'
              }}
            />
            <div 
              className="relative w-full h-[500px] overflow-hidden rounded-2xl"
              style={{
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 80px rgba(139, 92, 246, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <div 
                className="absolute"
                style={{
                  transform: `translateY(-${scrollOffset}px)`,
                  willChange: 'transform',
                  top: 0,
                  left: 0,
                  right: 0
                }}
              >
                <picture>
                  <source srcSet={blocks[2].imageWebP} type="image/webp" />
                  <img
                    src={blocks[2].image}
                    alt={blocks[2].title}
                    className="w-full h-auto block"
                    width={1920}
                    height={1080}
                    style={{ 
                      maxWidth: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </picture>
                {/* Dupliquer l'image pour un scroll continu */}
                <picture>
                  <source srcSet={blocks[2].imageWebP} type="image/webp" />
                  <img
                    src={blocks[2].image}
                    alt={blocks[2].title}
                    className="w-full h-auto block"
                    width={1920}
                    height={1080}
                    style={{ 
                      maxWidth: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </picture>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Box with Dots and Play/Pause - centrée avec plus d'espace */}
      <div className="flex justify-center mt-16">
        <div 
          className="inline-flex items-center gap-4 px-6 py-3 relative overflow-hidden"
          style={{ 
            backgroundColor: '#f8f8f8',
            borderRadius: '28px',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Play/Pause Button */}
          <button
            onClick={togglePlayPause}
            className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-200 transition-colors"
            aria-label={isPlaying ? t('landing.tools.pause') : t('landing.tools.play')}
          >
            {isPlaying ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="2" width="3" height="12" rx="1" fill="#000000"/>
                <rect x="10" y="2" width="3" height="12" rx="1" fill="#000000"/>
              </svg>
            ) : (
              <Play className="w-4 h-4 text-gray-900 ml-0.5" />
            )}
          </button>

          {/* Dots */}
          <div className="flex items-center gap-2">
            {blocks.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setActiveBlock(index);
                  setProgress(0);
                }}
                className="relative flex items-center justify-center min-w-[44px] min-h-[44px] overflow-visible transition-all duration-300"
                aria-label={`Aller au bloc ${index + 1}`}
              >
                <span
                  className={`relative overflow-hidden block rounded-full transition-all duration-300 ${
                    index === activeBlock
                      ? 'w-10 h-2.5 bg-transparent'
                      : 'w-2.5 h-2.5 bg-gray-400 hover:bg-gray-500'
                  }`}
                >
                  {index === activeBlock && isPlaying && (
                    <div
                      className="absolute inset-0 h-full transition-all duration-75 ease-linear bg-black"
                      style={{ width: `${progress}%`, borderRadius: '9999px' }}
                    />
                  )}
                  {index === activeBlock && !isPlaying && (
                    <div className="absolute inset-0 h-full w-full bg-black" style={{ borderRadius: '9999px' }} />
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

// Composant Carousel pour les fonctionnalités
const FeaturesCarousel = React.memo(() => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [previousSlide, setPreviousSlide] = useState<number | null>(null);
  const [slideDirection, setSlideDirection] = useState<1 | -1>(1);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 880);
  const slideTransitionDuration = 680;
  const slides = [
    {
      titleKey: 'journal',
      image: journalDemoImage,
      imageWebP: journalDemoDarkWebP,
      icon: (isActive: boolean) => (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transition-all duration-500 ease-out" style={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.58)', transform: isActive ? 'scale(1.05)' : 'scale(1)' }}>
          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
          <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
          <g id="SVGRepo_iconCarrier"> 
            <path d="M3 8C3 5.17157 3 3.75736 3.87868 2.87868C4.75736 2 6.17157 2 9 2H15C17.8284 2 19.2426 2 20.1213 2.87868C21 3.75736 21 5.17157 21 8V16C21 18.8284 21 20.2426 20.1213 21.1213C19.2426 22 17.8284 22 15 22H9C6.17157 22 4.75736 22 3.87868 21.1213C3 20.2426 3 18.8284 3 16V8Z" stroke="currentColor" strokeWidth="1.5"></path> 
            <path d="M8 2.5V22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M2 12H4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M2 16H4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M2 8H4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M11.5 6.5H16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M11.5 10H16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
          </g>
        </svg>
      )
    },
    {
      titleKey: 'backtest',
      image: backtestDemoImage,
      imageWebP: backtestDemoImageWebP,
      icon: (isActive: boolean) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transition-all duration-500 ease-out" style={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.58)', transform: isActive ? 'scale(1.05)' : 'scale(1)' }}>
          <g transform="matrix(0.700756 0 0 0.779158 8.19638 -17.126)">
            <path clipRule="evenodd" d="m13.69,23.89c0.21,0.18 0.21,0.47 0,0.64l-2.65,2.26l5.84,0c0.68,0 1.69,0.17 2.54,0.69c0.88,0.54 1.56,1.44 1.56,2.8c0,0.25 -0.24,0.46 -0.53,0.46c-0.29,0 -0.53,-0.21 -0.53,-0.46c0,-1.07 -0.51,-1.69 -1.11,-2.06c-0.63,-0.39 -1.41,-0.52 -1.92,-0.52l-5.84,0l2.65,2.26c0.21,0.18 0.21,0.47 0,0.64c-0.21,0.18 -0.55,0.18 -0.76,0l-3.56,-3.04c-0.21,-0.18 -0.21,-0.47 0,-0.64l3.56,-3.04c0.21,-0.18 0.55,-0.18 0.76,0l-0.01,0.01z" fill="currentColor" fillRule="evenodd"/>
          </g>
          <rect fill="currentColor" height="10.73" rx="0.2" width="3.06" x="0.01" y="9.24"/>
          <rect fill="currentColor" height="6.8" transform="matrix(1 0 0 1 0 0) matrix(7.1018 0 0 0.326515 -91.4601 4.95259)" width="0.08" x="13.84" y="16.4"/>
          <rect fill="currentColor" height="4.96" rx="0.2" transform="matrix(1 0 0 1 0 0) matrix(3.97357 0 0 1.50334 -43.6922 -13.4894)" width="0.74" x="12.43" y="17.3"/>
          <rect fill="currentColor" height="6.8" transform="matrix(7.1018 0 0 0.188149 -91.4601 4.98085)" width="0.08" x="14.59" y="15.16"/>
          <rect fill="currentColor" height="4.96" rx="0.2" transform="matrix(4.12419 0 0 0.757708 -46.8979 -11.1854)" width="0.74" x="14.02" y="26.77"/>
          <rect fill="currentColor" height="6.8" transform="matrix(7.1018 0 0 0.225074 -91.4601 16.8006)" width="0.08" x="15.26" y="15.3"/>
          <rect fill="currentColor" height="4.96" rx="0.2" transform="matrix(1 0 0 1 0 0) matrix(4.12419 0 0 1.50043 -46.8979 -16.1244)" width="0.74" x="15.17" y="19.3"/>
          <rect fill="currentColor" height="6.8" transform="matrix(7.1018 0 0 0.372617 -91.4601 14.4184)" width="0.08" x="16.01" y="15.84"/>
          <rect fill="currentColor" height="12.16" rx="0.2" width="3.06" x="20.91" y="8.18"/>
          <rect fill="currentColor" height="6.8" transform="matrix(7.1018 0 0 0.217272 -91.4601 16.3995)" width="0.08" x="13.84" y="16.4"/>
          <rect fill="currentColor" height="6.8" transform="matrix(7.1018 0 0 1.49395 -91.4601 -9.78967)" width="0.08" x="14.59" y="15.15"/>
          <rect fill="currentColor" height="6.8" transform="matrix(7.41099 0 0 2.34754 -85.6608 -123.745)" width="0.08" x="11.72" y="55.89"/>
        </svg>
      )
    },
    {
      titleKey: 'notes',
      image: notesDemoImage,
      imageWebP: notesDemoDarkWebP,
      icon: (isActive: boolean) => (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transition-all duration-500 ease-out" style={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.58)', transform: isActive ? 'scale(1.05)' : 'scale(1)' }}>
          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
          <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
          <g id="SVGRepo_iconCarrier"> 
            <path d="M20.3116 12.6473L20.8293 10.7154C21.4335 8.46034 21.7356 7.3328 21.5081 6.35703C21.3285 5.58657 20.9244 4.88668 20.347 4.34587C19.6157 3.66095 18.4881 3.35883 16.2331 2.75458C13.978 2.15033 12.8504 1.84821 11.8747 2.07573C11.1042 2.25537 10.4043 2.65945 9.86351 3.23687C9.27709 3.86298 8.97128 4.77957 8.51621 6.44561C8.43979 6.7254 8.35915 7.02633 8.27227 7.35057L8.27222 7.35077L7.75458 9.28263C7.15033 11.5377 6.84821 12.6652 7.07573 13.641C7.25537 14.4115 7.65945 15.1114 8.23687 15.6522C8.96815 16.3371 10.0957 16.6392 12.3508 17.2435L12.3508 17.2435C14.3834 17.7881 15.4999 18.0873 16.415 17.9744C16.5152 17.9621 16.6129 17.9448 16.7092 17.9223C17.4796 17.7427 18.1795 17.3386 18.7203 16.7612C19.4052 16.0299 19.7074 14.9024 20.3116 12.6473Z" stroke="currentColor" strokeWidth="1.5"></path> 
            <path d="M16.415 17.9741C16.2065 18.6126 15.8399 19.1902 15.347 19.6519C14.6157 20.3368 13.4881 20.6389 11.2331 21.2432C8.97798 21.8474 7.85044 22.1495 6.87466 21.922C6.10421 21.7424 5.40432 21.3383 4.86351 20.7609C4.17859 20.0296 3.87647 18.9021 3.27222 16.647L2.75458 14.7151C2.15033 12.46 1.84821 11.3325 2.07573 10.3567C2.25537 9.58627 2.65945 8.88638 3.23687 8.34557C3.96815 7.66065 5.09569 7.35853 7.35077 6.75428C7.77741 6.63996 8.16368 6.53646 8.51621 6.44531" stroke="currentColor" strokeWidth="1.5"></path> 
            <path d="M11.7769 10L16.6065 11.2941" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M11 12.8975L13.8978 13.6739" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
          </g>
        </svg>
      )
    },
    {
      titleKey: 'calendar',
      image: calendarDemoImage,
      imageWebP: calendarDemoDarkWebP,
      icon: (isActive: boolean) => (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transition-all duration-500 ease-out" style={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.58)', transform: isActive ? 'scale(1.05)' : 'scale(1)' }}>
          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
          <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
          <g id="SVGRepo_iconCarrier"> 
            <path d="M2 12C2 8.22876 2 6.34315 3.17157 5.17157C4.34315 4 6.22876 4 10 4H14C17.7712 4 19.6569 4 20.8284 5.17157C22 6.34315 22 8.22876 22 12V14C22 17.7712 22 19.6569 20.8284 20.8284C19.6569 22 17.7712 22 14 22H10C6.22876 22 4.34315 22 3.17157 20.8284C2 19.6569 2 17.7712 2 14V12Z" stroke="currentColor" strokeWidth="1.5"></path> 
            <path d="M7 4V2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M17 4V2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M2.5 9H21.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M18 17C18 17.5523 17.5523 18 17 18C16.4477 18 16 17.5523 16 17C16 16.4477 16.4477 16 17 16C17.5523 16 18 16.4477 18 17Z" fill="currentColor"></path> 
            <path d="M18 13C18 13.5523 17.5523 14 17 14C16.4477 14 16 13.5523 16 13C16 12.4477 16.4477 12 17 12C17.5523 12 18 12.4477 18 13Z" fill="currentColor"></path> 
            <path d="M13 17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17C11 16.4477 11.4477 16 12 16C12.5523 16 13 16.4477 13 17Z" fill="currentColor"></path> 
            <path d="M13 13C13 13.5523 12.5523 14 12 14C11.4477 14 11 13.5523 11 13C11 12.4477 11.4477 12 12 12C12.5523 12 13 12.4477 13 13Z" fill="currentColor"></path> 
            <path d="M8 17C8 17.5523 7.55228 18 7 18C6.44772 18 6 17.5523 6 17C6 16.4477 6.44772 16 7 16C7.55228 16 8 16.4477 8 17Z" fill="currentColor"></path> 
            <path d="M8 13C8 13.5523 7.55228 14 7 14C6.44772 14 6 13.5523 6 13C6 12.4477 6.44772 12 7 12C7.55228 12 8 12.4477 8 13Z" fill="currentColor"></path> 
          </g>
        </svg>
      )
    },
    {
      titleKey: 'calculator',
      image: calculatriceDemoImage,
      imageWebP: calculatriceDemoDarkWebP,
      icon: (isActive: boolean) => (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transition-all duration-500 ease-out" style={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.58)', transform: isActive ? 'scale(1.05)' : 'scale(1)' }}>
          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
          <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
          <g id="SVGRepo_iconCarrier"> 
            <path d="M4.31802 20.5355C5.63604 22 7.75736 22 12 22C16.2426 22 18.364 22 19.682 20.5355C21 19.0711 21 16.714 21 12C21 7.28595 21 4.92893 19.682 3.46447C18.364 2 16.2426 2 12 2C7.75736 2 5.63604 2 4.31802 3.46447C3 4.92893 3 7.28595 3 12C3 16.714 3 19.0711 4.31802 20.5355Z" stroke="currentColor" strokeWidth="1.5"></path> 
            <path d="M7 8C7 7.53501 7 7.30252 7.05111 7.11177C7.18981 6.59413 7.59413 6.18981 8.11177 6.05111C8.30252 6 8.53501 6 9 6H15C15.465 6 15.6975 6 15.8882 6.05111C16.4059 6.18981 16.8102 6.59413 16.9489 7.11177C17 7.30252 17 7.53501 17 8C17 8.46499 17 8.69748 16.9489 8.88823C16.8102 9.40587 16.4059 9.81019 15.8882 9.94889C15.6975 10 15.465 10 15 10H9C8.53501 10 8.30252 10 8.11177 9.94889C7.59413 9.81019 7.18981 9.40587 7.05111 8.88823C7 8.69748 7 8.46499 7 8Z" stroke="currentColor" strokeWidth="1.5"></path> 
            <circle cx="8" cy="13" r="1" fill="currentColor"></circle> 
            <circle cx="8" cy="17" r="1" fill="currentColor"></circle> 
            <circle cx="12" cy="13" r="1" fill="currentColor"></circle> 
            <circle cx="12" cy="17" r="1" fill="currentColor"></circle> 
            <circle cx="16" cy="13" r="1" fill="currentColor"></circle> 
            <circle cx="16" cy="17" r="1" fill="currentColor"></circle> 
          </g>
        </svg>
      )
    },
    {
      titleKey: 'seasonality',
      image: saionalityDemoImage,
      imageWebP: saionalityDemoDarkWebP,
      icon: (isActive: boolean) => (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transition-all duration-500 ease-out" style={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.58)', transform: isActive ? 'scale(1.05)' : 'scale(1)' }}>
          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
          <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
          <g id="SVGRepo_iconCarrier"> 
            <path d="M12 4L12 20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M16 7L16 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M8 7L8 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M20 11L20 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M4 11L4 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
          </g>
        </svg>
      )
    },
    {
      titleKey: 'retails',
      image: retailsDemoImage,
      imageWebP: retailsDemoDarkWebP,
      icon: (isActive: boolean) => (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transition-all duration-500 ease-out" style={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.58)', transform: isActive ? 'scale(1.05)' : 'scale(1)' }}>
          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
          <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
          <g id="SVGRepo_iconCarrier"> 
            <path d="M22 7L14.6203 14.3347C13.6227 15.3263 13.1238 15.822 12.5051 15.822C11.8864 15.8219 11.3876 15.326 10.3902 14.3342L10.1509 14.0962C9.15254 13.1035 8.65338 12.6071 8.03422 12.6074C7.41506 12.6076 6.91626 13.1043 5.91867 14.0977L2 18M22 7V12.5458M22 7H16.4179" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path> 
          </g>
        </svg>
      )
    },
    {
      titleKey: 'tags',
      image: tagsDemoImage,
      imageWebP: tagsDemoDarkWebP,
      icon: (isActive: boolean) => (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transition-all duration-500 ease-out" style={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.58)', transform: isActive ? 'scale(1.05)' : 'scale(1)' }}>
          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
          <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
          <g id="SVGRepo_iconCarrier"> 
            <path d="M4.72848 16.1369C3.18295 14.5914 2.41018 13.8186 2.12264 12.816C1.83509 11.8134 2.08083 10.7485 2.57231 8.61875L2.85574 7.39057C3.26922 5.59881 3.47597 4.70292 4.08944 4.08944C4.70292 3.47597 5.59881 3.26922 7.39057 2.85574L8.61875 2.57231C10.7485 2.08083 11.8134 1.83509 12.816 2.12264C13.8186 2.41018 14.5914 3.18295 16.1369 4.72848L17.9665 6.55812C20.6555 9.24711 22 10.5916 22 12.2623C22 13.933 20.6555 15.2775 17.9665 17.9665C15.2775 20.6555 13.933 22 12.2623 22C10.5916 22 9.24711 20.6555 6.55812 17.9665L4.72848 16.1369Z" stroke="currentColor" strokeWidth="1.5"></path> 
            <circle cx="8.60724" cy="8.87891" r="2" transform="rotate(-45 8.60724 8.87891)" stroke="currentColor" strokeWidth="1.5"></circle> 
            <path d="M11.5417 18.5L18.5208 11.5208" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
          </g>
        </svg>
      )
    },
    {
      titleKey: 'plans',
      image: plansDemoImage,
      imageWebP: plansDemoDarkWebP,
      icon: (isActive: boolean) => (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transition-all duration-500 ease-out" style={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.58)', transform: isActive ? 'scale(1.05)' : 'scale(1)' }}>
          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
          <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
          <g id="SVGRepo_iconCarrier"> 
            <path d="M18.18 8.03933L18.6435 7.57589C19.4113 6.80804 20.6563 6.80804 21.4241 7.57589C22.192 8.34374 22.192 9.58868 21.4241 10.3565L20.9607 10.82M18.18 8.03933C18.18 8.03933 18.238 9.02414 19.1069 9.89309C19.9759 10.762 20.9607 10.82 20.9607 10.82M18.18 8.03933L13.9194 12.2999C13.6308 12.5885 13.4865 12.7328 13.3624 12.8919C13.2161 13.0796 13.0906 13.2827 12.9882 13.4975C12.9014 13.6797 12.8368 13.8732 12.7078 14.2604L12.2946 15.5L12.1609 15.901M20.9607 10.82L16.7001 15.0806C16.4115 15.3692 16.2672 15.5135 16.1081 15.6376C15.9204 15.7839 15.7173 15.9094 15.5025 16.0118C15.3203 16.0986 15.1268 16.1632 14.7396 16.2922L13.5 16.7054L13.099 16.8391M13.099 16.8391L12.6979 16.9728C12.5074 17.0363 12.2973 16.9867 12.1553 16.8447C12.0133 16.7027 11.9637 16.4926 12.0272 16.3021L12.1609 15.901M13.099 16.8391L12.1609 15.901" stroke="currentColor" strokeWidth="1.5"></path> 
            <path d="M8 13H10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M8 9H14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M8 17H9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
            <path d="M19.8284 3.17157C18.6569 2 16.7712 2 13 2H11C7.22876 2 5.34315 2 4.17157 3.17157C3 4.34315 3 6.22876 3 10V14C3 17.7712 3 19.6569 4.17157 20.8284C5.34315 22 7.22876 22 11 22H13C16.7712 22 18.6569 22 19.8284 20.8284C20.7715 19.8853 20.9554 18.4796 20.9913 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path> 
          </g>
        </svg>
      )
    },
    {
      titleKey: 'ai',
      image: iaDemoImage,
      imageWebP: iaDemoDarkWebP,
      icon: (isActive: boolean) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6 transition-all duration-500 ease-out" style={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.58)', transform: isActive ? 'scale(1.05)' : 'scale(1)' }}>
          <path d="M12 18V5"/>
          <path d="M15 13a4.17 4.17 0 0 1-3-4 4.17 4.17 0 0 1-3 4"/>
          <path d="M17.598 6.5A3 3 0 1 0 12 5a3 3 0 1 0-5.598 1.5"/>
          <path d="M17.997 5.125a4 4 0 0 1 2.526 5.77"/>
          <path d="M18 18a4 4 0 0 0 2-7.464"/>
          <path d="M19.967 17.483A4 4 0 1 1 12 18a4 4 0 1 1-7.967-.517"/>
          <path d="M6 18a4 4 0 0 1-2-7.464"/>
          <path d="M6.003 5.125a4 4 0 0 0-2.526 5.77"/>
        </svg>
      )
    }
  ];

  // Auto-rotation du carrousel toutes les 5 secondes avec progress bar
  useEffect(() => {
    if (!isPlaying) {
      setProgress(0);
      return;
    }
    
    setProgress(0); // Reset au début
    let startTime = Date.now();
    const duration = 5000; // 5 secondes
    
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / duration) * 100, 100);
      
      setProgress(newProgress);
      
      if (newProgress >= 100) {
        setPreviousSlide(currentSlide);
        setSlideDirection(1);
        setCurrentSlide((currentSlide + 1) % slides.length);
      }
    }, 50);

    return () => clearInterval(progressInterval);
  }, [slides.length, isPlaying, currentSlide]);

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const transitionToSlide = (nextIndex: number, direction: 1 | -1) => {
    if (nextIndex === currentSlide) {
      return;
    }

    setPreviousSlide(currentSlide);
    setSlideDirection(direction);
    setCurrentSlide(nextIndex);
    setProgress(0);
  };

  const goToPrevious = () => {
    transitionToSlide(currentSlide === 0 ? slides.length - 1 : currentSlide - 1, -1);
  };

  const goToNext = () => {
    transitionToSlide((currentSlide + 1) % slides.length, 1);
  };

  const suppressMouseFocus = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  // Détecter si on est sur mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 880);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Créer un tableau étendu pour la boucle infinie sur mobile
  const extendedSlides = isMobile 
    ? [...slides.slice(-1), ...slides, ...slides.slice(0, 1)] // [dernier, ...tous, premier]
    : slides;
  
  // Index ajusté pour le tableau étendu (décalé de 1 sur mobile)
  const adjustedIndex = isMobile ? currentSlide + 1 : currentSlide;

  useEffect(() => {
    slides.forEach((slide) => {
      const preloadImage = new Image();
      preloadImage.src = slide.image;

      if (slide.imageWebP) {
        const preloadWebP = new Image();
        preloadWebP.src = slide.imageWebP;
      }
    });
  }, []);

  // Gérer le défilement des icônes sur mobile - centrer l'élément actif
  useEffect(() => {
    // Sur mobile, on centre toujours l'élément actif
    // Le conteneur affichera seulement les éléments qui rentrent complètement
    setVisibleStartIndex(currentSlide);
  }, [currentSlide]);

  // Reset progress quand on change de slide manuellement
  useEffect(() => {
    if (!isPlaying) {
      setProgress(0);
    }
  }, [currentSlide, isPlaying]);

  useEffect(() => {
    if (previousSlide === null) {
      return;
    }

    const cleanupTimeout = window.setTimeout(() => {
      setPreviousSlide(null);
    }, slideTransitionDuration);

    return () => window.clearTimeout(cleanupTimeout);
  }, [previousSlide, currentSlide]);

  return (
    <div className="relative w-full max-w-6xl mx-auto mt-6 sm:mt-8" style={{ overflowAnchor: 'none' }}>
      {/* Glow effect derrière le carousel - très subtil */}
      <div 
        className="absolute inset-0 blur-3xl opacity-10 rounded-2xl pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3), rgba(251, 146, 60, 0.2))',
          transform: 'scale(0.9)',
          animation: 'pulse 4s ease-in-out infinite'
        }}
      />
      
      {/* Navigation avec icônes et titres avec flèches */}
      <div className="relative flex items-center justify-center gap-6 sm:gap-8 md:gap-12 mb-8 px-4 max-w-5xl mx-auto" style={{ minHeight: isMobile ? '104px' : '116px' }}>
        {/* Flèche gauche */}
        <AnimatedText delay={50} duration={600}>
          <button
            onMouseDown={suppressMouseFocus}
            onClick={goToPrevious}
            className="flex-shrink-0 flex items-center justify-center transition-all duration-300 hover:scale-110"
            aria-label={t('landing.tools.prev_slide')}
          >
            <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'rgba(255,255,255,0.7)' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </AnimatedText>

        {/* Icônes de navigation - Conteneur avec overflow pour mobile */}
        <div className="flex-1 lg:max-w-none overflow-x-hidden lg:overflow-visible py-6 lg:py-4 scrollbar-hide" style={{ maxWidth: isMobile ? '340px' : 'none', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <div 
            className="flex items-center justify-start lg:justify-center gap-4 sm:gap-6 md:gap-8 transition-transform duration-500 ease-in-out"
            style={{
              transform: isMobile ? `translateX(calc(125px - ${adjustedIndex * 110}px))` : 'none'
            }}
          >
            {extendedSlides.map((slide, index) => {
              // Calculer si cette icône est active (en tenant compte du décalage sur mobile)
              const isActive = isMobile ? index === adjustedIndex : index === currentSlide;
              
              return (
                <AnimatedText key={`${slide.titleKey}-${index}`} delay={100 + (index * 100)} duration={600}>
                  <button
                    onMouseDown={suppressMouseFocus}
                    onClick={() => {
                      if (isMobile) {
                        // Sur mobile, mapper l'index étendu vers l'index réel
                        if (index === 0) {
                          transitionToSlide(slides.length - 1, -1); // Dernier slide
                        } else if (index === extendedSlides.length - 1) {
                          transitionToSlide(0, 1); // Premier slide
                        } else {
                          transitionToSlide(index - 1, index - 1 > currentSlide ? 1 : -1); // Index normal - 1
                        }
                      } else {
                        transitionToSlide(index, index > currentSlide ? 1 : -1);
                      }
                    }}
                    className={`flex flex-col items-center gap-2 transition-all duration-300 flex-shrink-0 ${
                      isActive
                        ? 'scale-110'
                        : 'hover:scale-105'
                    }`}
                    style={{ width: '90px', minHeight: '82px', color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.55)' }}
                  >
                    <div 
                      className={`flex items-center justify-center transition-all duration-300 ${
                        isActive
                          ? 'rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] shadow-lg'
                          : 'bg-transparent border-none shadow-none'
                      }`}
                      style={{ width: '48px', height: '48px' }}
                    >
                      {slide.icon(isActive)}
                    </div>
                    {isActive ? (
                      <span
                        className="text-xs sm:text-sm font-medium text-center whitespace-nowrap"
                        style={{
                          color: '#FFFFFF',
                          opacity: 1,
                          WebkitTextFillColor: '#FFFFFF'
                        }}
                      >
                        {t(`landing.tools.slides.${slide.titleKey}`)}
                      </span>
                    ) : (
                      <span
                        className="text-xs sm:text-sm font-medium text-center whitespace-nowrap transition-colors duration-300"
                        style={{
                          color: 'rgba(255,255,255,0.55)',
                          opacity: 1,
                          WebkitTextFillColor: 'rgba(255,255,255,0.55)'
                        }}
                      >
                        {t(`landing.tools.slides.${slide.titleKey}`)}
                      </span>
                    )}
                  </button>
                </AnimatedText>
              );
            })}
          </div>
        </div>

        {/* Flèche droite */}
        <AnimatedText delay={100 + (slides.length * 100)} duration={600}>
          <button
            onMouseDown={suppressMouseFocus}
            onClick={goToNext}
            className="flex-shrink-0 flex items-center justify-center transition-all duration-300 hover:scale-110"
            aria-label={t('landing.tools.next_slide')}
          >
            <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'rgba(255,255,255,0.7)' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </AnimatedText>
      </div>

      {/* Carousel Container */}
      <div className="relative w-full overflow-hidden" style={{ overflowAnchor: 'none' }}>
        <div className="w-full px-2 sm:px-12 lg:px-16 py-2 sm:py-4">
          <div className="w-full rounded-2xl p-3 sm:p-6">
            <div className="w-full flex justify-center items-center">
              <div className="w-full max-w-[650px] sm:max-w-[650px] md:max-w-[900px] lg:max-w-[1200px] xl:max-w-[1400px]">
                <div className="relative w-full h-[220px] sm:h-[300px] md:h-[420px] lg:h-[560px] xl:h-[680px] overflow-hidden" style={{ overflowAnchor: 'none', contain: 'layout paint size' }}>
                  {slides.map((slide, index) => (
                    (() => {
                      const isCurrent = index === currentSlide;
                      const isPrevious = index === previousSlide;
                      const directionOffset = slideDirection === 1 ? 14 : -14;

                      return (
                    <div
                      key={index}
                      className="absolute inset-0"
                      style={{
                        opacity: isCurrent ? 1 : isPrevious ? 0 : 0,
                        transform: isCurrent
                          ? 'translate3d(0, 0, 0) scale(1)'
                          : isPrevious
                            ? `translate3d(${-directionOffset}px, 0, 0) scale(0.996)`
                            : `translate3d(${directionOffset}px, 0, 0) scale(1.008)`,
                        filter: isCurrent ? 'blur(0px)' : isPrevious ? 'blur(1.5px)' : 'blur(2px)',
                        zIndex: isCurrent ? 2 : isPrevious ? 1 : 0,
                        pointerEvents: isCurrent ? 'auto' : 'none',
                        transition: `opacity ${slideTransitionDuration}ms cubic-bezier(0.25, 0.1, 0.25, 1), transform ${slideTransitionDuration}ms cubic-bezier(0.25, 0.1, 0.25, 1), filter ${slideTransitionDuration - 80}ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
                        willChange: 'opacity, transform, filter'
                      }}
                    >
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="relative max-w-full max-h-full">
                          <div
                            className="absolute inset-0 rounded-xl"
                            style={{
                              opacity: isCurrent ? 0 : 0.06,
                              background: slideDirection === 1
                                ? 'linear-gradient(90deg, rgba(11,11,12,0.08) 0%, rgba(11,11,12,0) 36%)'
                                : 'linear-gradient(270deg, rgba(11,11,12,0.08) 0%, rgba(11,11,12,0) 36%)',
                              transition: `opacity ${slideTransitionDuration}ms cubic-bezier(0.25, 0.1, 0.25, 1)`
                            }}
                          />
                          {slide.imageWebP ? (
                            <picture style={{ display: 'block' }}>
                              <source srcSet={slide.imageWebP} type="image/webp" />
                              <img
                                src={slide.image}
                                alt={t(`landing.tools.slides.${slide.titleKey}`)}
                                className="block rounded-xl"
                                loading="eager"
                                decoding="sync"
                                width={1920}
                                height={1080}
                                style={{
                                  display: 'block',
                                  width: 'auto',
                                  height: 'auto',
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  borderRadius: '16px',
                                  transform: isCurrent ? 'scale(1)' : 'scale(1.008)',
                                  transition: `transform ${slideTransitionDuration + 40}ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
                                  willChange: 'transform'
                                }}
                              />
                            </picture>
                          ) : (
                            <img
                              src={slide.image}
                              alt={t(`landing.tools.slides.${slide.titleKey}`)}
                              className="block rounded-xl"
                              loading="eager"
                              decoding="sync"
                              width={1920}
                              height={1080}
                              style={{
                                display: 'block',
                                width: 'auto',
                                height: 'auto',
                                maxWidth: '100%',
                                maxHeight: '100%',
                                borderRadius: '16px',
                                transform: isCurrent ? 'scale(1)' : 'scale(1.008)',
                                transition: `transform ${slideTransitionDuration + 40}ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
                                willChange: 'transform'
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                      );
                    })()
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Box with Dots and Play/Pause */}
      <div className="flex justify-center mt-4">
        <div 
          className="inline-flex items-center gap-2 px-4 py-2.5 relative overflow-hidden"
          style={{ 
            backgroundColor: 'rgba(18, 18, 20, 0.92)',
            borderRadius: '9999px',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '0 18px 45px rgba(0, 0, 0, 0.38)',
          }}
        >
          <button
            onMouseDown={suppressMouseFocus}
            onClick={togglePlayPause}
            className="flex items-center justify-center w-8 h-8 rounded-full transition-colors"
            aria-label={isPlaying ? t('landing.tools.pause') : t('landing.tools.play')}
            style={{ color: 'rgba(255,255,255,0.9)' }}
          >
            {isPlaying ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="2" width="3" height="12" rx="1" fill="currentColor"/>
                <rect x="10" y="2" width="3" height="12" rx="1" fill="currentColor"/>
              </svg>
            ) : (
              <Play className="w-4 h-4 ml-0.5" />
            )}
          </button>

          <div className="flex items-center gap-0.5">
            {slides.map((_, index) => (
              <button
                key={index}
                onMouseDown={suppressMouseFocus}
                onClick={() => {
                  transitionToSlide(index, index > currentSlide ? 1 : -1);
                }}
                className="relative flex items-center justify-center min-w-[24px] min-h-[24px] overflow-visible transition-all duration-300"
                aria-label={t('landing.tools.go_to_slide', { index: index + 1 })}
              >
                <span
                  className={`relative overflow-hidden block rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? 'w-12 h-3 bg-transparent'
                      : 'w-2 h-2 bg-[rgba(255,255,255,0.28)] hover:bg-[rgba(255,255,255,0.45)]'
                  }`}
                >
                  {index === currentSlide && isPlaying && (
                    <div
                      className="absolute inset-0 h-full transition-all duration-75 ease-linear"
                      style={{ width: `${progress}%`, borderRadius: '9999px', background: 'linear-gradient(135deg, #FFFFFF 0%, #D1D5DB 100%)' }}
                    />
                  )}
                  {index === currentSlide && !isPlaying && (
                    <div className="absolute inset-0 h-full w-full" style={{ borderRadius: '9999px', background: 'linear-gradient(135deg, #FFFFFF 0%, #D1D5DB 100%)' }} />
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

// ── Landing page principale : capture UTM au chargement du module ──
// N'écrase lead_source que si un utm_source/utm_campaign est présent dans l'URL.
// Si pas de paramètre UTM → les cookies existants sont conservés intacts.
// IMPORTANT : registerAcquisitionParams() doit être appelé AVANT history.replaceState,
// sinon PostHog (capture différée derrière le chargement de l'auth) ne voit plus l'URL.
(() => {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = params.get('utm_source') || params.get('utm_campaign');
    if (!utm) return; // Pas d'UTM → on ne touche pas aux cookies existants
    const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `lead_source=${encodeURIComponent(utm)}; path=/; expires=${expires}; SameSite=Lax`;
    registerAcquisitionParams();
    // Nettoyer l'URL
    window.history.replaceState({}, '', window.location.pathname);
  } catch { /* silently ignore */ }
})();

/**
 * Texte qui doit tenir sur `maxLines` ligne(s) : mesure le nombre réel de lignes
 * (`getClientRects()` = une boîte par ligne pour un inline) et réduit la police par
 * dichotomie jusqu'à ce que ça rentre. Indispensable car la longueur varie selon la
 * langue (« FocusPips te dit exactement où. » = 31 car., l'espagnol en fait 36 ; le
 * sous-titre allemand fait 164 car.) et selon l'écran.
 *
 * - `only`   : n'agit que sous sm (mobile) ou qu'à partir de sm (desktop) ; hors de
 *              sa plage, aucune taille inline n'est posée → classes/styles existants.
 * - `maxRem` : taille de départ ; si absent, la taille calculée du parent (respecte
 *              donc un `clamp()` déjà défini sur le parent).
 * - Rend un `<span>` inline : ne pas lui donner `display:block` (une seule boîte).
 */
function FitText({ children, maxRem, minRem = 0.9, maxLines = 1, only = 'mobile', className = '', style }: {
  children: React.ReactNode;
  maxRem?: number;
  minRem?: number;
  maxLines?: number;
  only?: 'mobile' | 'desktop';
  className?: string;
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;

    const lines = () => el.getClientRects().length;
    const fit = () => {
      const desktop = window.matchMedia('(min-width: 640px)').matches;
      if (only === 'mobile' ? desktop : !desktop) {
        el.style.fontSize = '';
        return;
      }
      el.style.fontSize = '';
      const maxPx = maxRem ? maxRem * 16 : parseFloat(getComputedStyle(parent).fontSize) || 16;
      const minPx = minRem * 16;
      el.style.fontSize = `${maxPx}px`;
      if (lines() <= maxLines) return;
      let lo = minPx; // plus grande taille connue qui tient (plancher sinon)
      let hi = maxPx; // ne tient pas
      for (let i = 0; i < 8; i++) {
        const mid = (lo + hi) / 2;
        el.style.fontSize = `${mid}px`;
        if (lines() > maxLines) hi = mid; else lo = mid;
      }
      el.style.fontSize = `${lo}px`;
    };

    fit();
    // Re-mesure quand Poppins arrive (les métriques de la police de repli diffèrent)
    document.fonts?.ready.then(fit).catch(() => {});
    const ro = new ResizeObserver(fit);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [maxRem, minRem, maxLines, only, children]);

  return (
    <span ref={ref} className={className} style={style}>
      {children}
    </span>
  );
}

export function LandingPage() {
  // console.log supprimé

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { 
    user, 
    isPremiumOnlyMode, 
    premiumOnlyActivationDate, 
    userCreatedAt,
    isPremium: authIsPremium,
    isTrial,
    subscriptionType: authSubscriptionType,
  } = useAuth();
  const { theme } = useTheme();
  const [showTerms, setShowTerms] = useState(false);
  const [loadingUpgrade, setLoadingUpgrade] = useState(false);
  const [loadingDowngrade, setLoadingDowngrade] = useState(false);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('yearly');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [isPremium, setIsPremium] = useState(false);
  const [isInAcademy, setIsInAcademy] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState<string | null>(null);

  // ── Bannière d'annonce du module Prop Firm ───────────────────────────────────
  // Elle vit dans le conteneur fixe, au-dessus de la navbar, non-fermable — même
  // gabarit que la bannière Black Friday (fond noir, texte gras, pas de chrome).
  // Elle renvoie vers la section #propfirm plutôt que vers /register : l'objectif
  // est de nourrir le funnel existant, pas de concurrencer le CTA du hero.
  const scrollToPropFirm = useCallback(() => {
    trackEvent('cta_banner_clicked', { position: 'banner_propfirm', label: 'Découvrir le cockpit prop firm' });
    document.getElementById('propfirm')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Partners
  interface LandingPartner { id: string; name: string; photoURL?: string; bio?: string; channels?: Record<string, string>; subscribers?: Record<string, number>; order?: number; localPhoto?: string; localPhotoWebP?: string; localPhotoSmWebP?: string; }
  const [landingPartners, setLandingPartners] = useState<LandingPartner[] | null>(null);
  const [partnerActive, setPartnerActive] = useState(0);
  const [partnerMobileIndex, setPartnerMobileIndex] = useState(0);
  const [partnerMobileTransitionEnabled, setPartnerMobileTransitionEnabled] = useState(true);
  const partnerTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const partnerMobileResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    setLandingPartners([
      {
        id: 'hugofx',
        name: 'HugoFX',
        bio: t('landing.partners.bios.hugofx'),
        channels: { Instagram: '@hugo_forex', Telegram: '@hugofxcanal', TikTok: '@hugo_forex', Youtube: 'https://youtube.com/@hugoforex' },
        subscribers: { Instagram: 13000, Telegram: 8905, TikTok: 24700, Youtube: 106000 },
        order: 1,
        localPhoto: partenaireImg1Png,
        localPhotoWebP: partenaireImg1WebP,
      },
      {
        id: 'chloefx',
        name: 'ChloéFX',
        bio: t('landing.partners.bios.chloefx'),
        channels: { Instagram: '@thepinkytrader', Telegram: '@chloeefx', TikTok: '@chloee.fx', YouTube: 'https://youtube.com/@thepinkytrader' },
        subscribers: { Instagram: 800, Telegram: 3000, TikTok: 14300, YouTube: 1000 },
        order: 2,
        localPhoto: partenaireImg3Png,
        localPhotoWebP: partenaireImg3WebP,
        localPhotoSmWebP: partenaireImg3SmWebP,
      },
      {
        id: 'zefrenchtrader',
        name: 'ZeFrenchTrader',
        bio: t('landing.partners.bios.zefrenchtrader'),
        channels: { Instagram: 'zefrenchtrader', Telegram: '@zefrenchtrader', TikTok: 'zefrenchtrader', YouTube: 'https://youtube.com/@zefrenchtrader' },
        subscribers: { Instagram: 730, Telegram: 1200, TikTok: 3476, YouTube: 2000 },
        order: 4,
        localPhoto: partenaireImg2Png,
        localPhotoWebP: partenaireImg2WebP,
      },
    ]);
    // Les bios sont traduites : ce bloc doit être rejoué à chaque changement de langue.
  }, [t]);

  const isMobilePartnerViewport = () => typeof window !== 'undefined' && window.innerWidth < 640;

  const recenterPartnerMobileTrack = (total: number, activeIndex: number) => {
    if (partnerMobileResetRef.current) clearTimeout(partnerMobileResetRef.current);
    partnerMobileResetRef.current = setTimeout(() => {
      setPartnerMobileTransitionEnabled(false);
      setPartnerMobileIndex(total + activeIndex);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setPartnerMobileTransitionEnabled(true);
        });
      });
      partnerMobileResetRef.current = null;
    }, 560);
  };

  useEffect(() => {
    if (!landingPartners || landingPartners.length === 0) return;
    setPartnerMobileTransitionEnabled(false);
    setPartnerMobileIndex(landingPartners.length + partnerActive);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setPartnerMobileTransitionEnabled(true);
      });
    });
  }, [landingPartners]);

  // Auto-rotate partners carousel
  useEffect(() => {
    if (!landingPartners || landingPartners.length <= 1) return;
    partnerTimerRef.current = setInterval(() => {
      if (isMobilePartnerViewport()) {
        setPartnerMobileTransitionEnabled(true);
        setPartnerMobileIndex(prev => prev + 1);
        setPartnerActive(prev => {
          const next = (prev + 1) % landingPartners.length;
          recenterPartnerMobileTrack(landingPartners.length, next);
          return next;
        });
        return;
      }
      setPartnerActive(prev => (prev + 1) % landingPartners.length);
    }, 4000);
    return () => {
      if (partnerTimerRef.current) clearInterval(partnerTimerRef.current);
      if (partnerMobileResetRef.current) clearTimeout(partnerMobileResetRef.current);
    };
  }, [landingPartners]);

  const partnerGoTo = (i: number) => {
    if (partnerTimerRef.current) clearInterval(partnerTimerRef.current);
    if (partnerMobileResetRef.current) clearTimeout(partnerMobileResetRef.current);

    if (landingPartners && landingPartners.length > 1 && isMobilePartnerViewport()) {
      const total = landingPartners.length;
      const normalizedIndex = ((i % total) + total) % total;
      const forwardDistance = (normalizedIndex - partnerActive + total) % total;
      const backwardDistance = (partnerActive - normalizedIndex + total) % total;
      const stepDistance = Math.min(forwardDistance, backwardDistance);

      if (stepDistance === 1 || total === 2) {
        const direction = forwardDistance <= backwardDistance ? 1 : -1;
        setPartnerMobileTransitionEnabled(true);
        setPartnerMobileIndex(prev => prev + direction);
        setPartnerActive(normalizedIndex);
        recenterPartnerMobileTrack(total, normalizedIndex);
      } else {
        setPartnerMobileTransitionEnabled(false);
        setPartnerMobileIndex(total + normalizedIndex);
        setPartnerActive(normalizedIndex);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setPartnerMobileTransitionEnabled(true);
          });
        });
      }
    } else {
      setPartnerActive(i);
    }

    if (landingPartners && landingPartners.length > 1) {
      partnerTimerRef.current = setInterval(() => {
        if (isMobilePartnerViewport()) {
          setPartnerMobileTransitionEnabled(true);
          setPartnerMobileIndex(prev => prev + 1);
          setPartnerActive(prev => {
            const next = (prev + 1) % (landingPartners?.length ?? 1);
            recenterPartnerMobileTrack(landingPartners.length, next);
            return next;
          });
          return;
        }
        setPartnerActive(prev => (prev + 1) % (landingPartners?.length ?? 1));
      }, 4000);
    }
  };


  // Vérifier si l'utilisateur est inscrit avant l'activation du mode premium uniquement
  const isUserRegisteredBeforePremiumOnly = userCreatedAt && premiumOnlyActivationDate 
    ? userCreatedAt < premiumOnlyActivationDate 
    : false;

  // Scroll vers l'ancre du hash au chargement de la page
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const scrollToHash = () => {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    // Petit délai pour laisser le temps au DOM de se rendre complètement
    const timer = setTimeout(scrollToHash, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // console.log supprimé
    const checkStatus = async () => {
      if (user) {
        // Vérifier directement dans Firestore si l'utilisateur est premium
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        
        setIsPremium(userData?.isPremium === true);
        setIsInAcademy(userData?.inAcademy === true);
        setSubscriptionType(userData?.subscriptionType || null);
      }
    };
    checkStatus();
  }, [user]);

  // Effet pour détecter et enregistrer l'affiliation depuis l'URL
  useEffect(() => {
    const detectAndSaveAffiliation = async () => {
      const queryParams = new URLSearchParams(window.location.search);
      const affIdFromUrl = queryParams.get('ref');
      const codeFromUrl = queryParams.get('code');

      if (affIdFromUrl) {
        try {
          // console.log supprimé
          
          // Valider l'ID via le backend
          const validationResult = await affiliateService.validateAffiliateId(affIdFromUrl);
          // console.log supprimé

          if (validationResult?.isValid) {
            // L'ID est valide, enregistrer dans un cookie de 30 jours
            // console.log supprimé
            
            saveAffiliateCookie({
              affiliateId: affIdFromUrl,
              affiliateName: validationResult.affiliateName,
              code: codeFromUrl || undefined
            });

            // console.log supprimé
          } else {
            // console.log supprimé
          }
        } catch (error) {
          // console.error supprimé
        }
      }
    };

    detectAndSaveAffiliation();
  }, []); // Se déclenche une seule fois au montage du composant

  // Optimisation des fonctions de rappel avec useCallback
  const handlePortal = useCallback(async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setLoadingDowngrade(true);
      const response = await getCustomerPortalSession(user.uid);
      if (response && response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      // console.error supprimé
      setToast({
        message: t('landing.errors.portal'),
        type: 'error'
      });
    } finally {
      setLoadingDowngrade(false);
    }
  }, [user, navigate, setLoadingDowngrade, setToast, t]);

  // Prix pour les membres de l'académie
  const academyPrices = {
    premium: {
      monthly: "15,99€",
      yearly: "13,33€"
    },
    expert: {
      monthly: "21,99€", 
      yearly: "18,33€"
    }
  };

  // Prix pour les non-membres de l'académie
  const regularPrices = {
    premium: {
      monthly: "15,99€",
      yearly: "13,33€"
    },
    expert: {
      monthly: "21,99€",
      yearly: "18,33€"
    }
  };

  const currentPrices = isInAcademy ? academyPrices : regularPrices;

  const ctaState = useMemo(
    () =>
      getPricingCtaState({
        isLoggedIn: !!user,
        isPremium: authIsPremium,
        isTrial,
        subscriptionType: authSubscriptionType,
      }),
    [user, authIsPremium, isTrial, authSubscriptionType],
  );

  const handleLandingBasicCta = useCallback(() => {
    if (ctaState.basicAction === 'none') return;
    if (ctaState.basicAction === 'portal') {
      window.location.href = STRIPE_BILLING_PORTAL;
      return;
    }
    if (ctaState.basicAction === 'register') {
      navigate('/register');
      return;
    }
    navigate('/dashboard');
  }, [ctaState.basicAction, navigate]);

  const handleLandingPremiumCta = useCallback(() => {
    if (ctaState.premiumAction === 'none') return;
    if (ctaState.premiumAction === 'portal') {
      window.location.href = STRIPE_BILLING_PORTAL;
      return;
    }
    if (ctaState.premiumAction === 'register') {
      navigate('/register');
      return;
    }
    window.location.href = buildPremiumStripeCheckoutUrl(billingInterval, user?.email);
  }, [ctaState.premiumAction, navigate, billingInterval, user?.email]);

  const scrollToPricing = () => {
    const pricingSection = document.querySelector('.mt-6.sm\\:mt-8.grid');
    if (pricingSection) {
      const offset = window.innerHeight * 0.15; // 15% de la hauteur de l'écran
      const elementPosition = pricingSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Memoization des données statiques
  const memoizedFeatures = useMemo(() => features.map(feature => (
    <FeatureCard
      key={feature.title}
      icon={feature.icon}
      title={feature.title}
      description={feature.description}
    />
  )), []);

  const getPremiumPrice = () => {
    return billingInterval === 'monthly' 
      ? PRICING_CONFIG.products.premium.monthlyPrice
      : PRICING_CONFIG.products.premium.yearlyPrice;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };


  const images = {
    logo: {
      src: logoSvg,
      srcWebP: logoSvgWebP,
      loading: 'eager',
      priority: true
    },
    ia: {
      src: IAImage,
      srcWebP: IAImageWebP,
      loading: 'lazy'
    },
    mainFeatures: {
      src: mainFeatures,
      loading: 'lazy'
    },
    dashboardMain: {
      src: dashboardMainImage,
      srcWebP: dashboardMainImageWebP,
      loading: 'eager',
      priority: true
    },
    trustpilot: {
      src: trustpilotLogo,
      srcWebP: trustpilotLogoWebP,
      loading: 'lazy'
    }
  };

  // Précharger les images critiques
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = images.logo.src;
    document.head.appendChild(link);
  }, []);

  // Dans la section plateformes compatibles, prépare la liste des logos :
  const platformLogos = [
    {
      href: 'https://www.metatrader4.com/fr',
      src: metatrader4Logo,
      srcWebP: metatrader4LogoWebP,
      alt: 'MetaTrader 4',
    },
    {
      href: 'https://www.metatrader5.com/fr',
      src: metatrader5Logo,
      srcWebP: metatrader5LogoWebP,
      alt: 'MetaTrader 5',
    },
    {
      href: 'https://ctrader.com/',
      src: cTraderLogo,
      srcWebP: cTraderLogoWebP,
      alt: 'cTrader',
    },
    {
      href: 'https://fr.tradingview.com/',
      src: tradingviewLogo,
      srcWebP: tradingviewLogoWebP,
      alt: 'TradingView',
    },
    {
      href: 'https://tradelocker.com/',
      src: tradelockerLogo,
      srcWebP: tradelockerLogoWebP,
      alt: 'TradeLocker',
    },
    {
      href: 'https://tradovate.com/',
      src: tradovateLogo,
      srcWebP: tradovateLogoWebP,
      alt: 'Tradovate',
    },
    {
      href: 'https://account.ninjatrader.com/register?introducingPartner=FocusPips',
      src: ninjaTraderLogo,
      srcWebP: ninjaTraderLogoWebP,
      alt: 'NinjaTrader',
    },
  ];

  // Forcer le mode clair au chargement de la page
  useEffect(() => {
    const forceLightMode = () => {
      document.documentElement.classList.remove('dark');
      document.documentElement.setAttribute('data-theme', 'light');
      document.body.classList.remove('dark');
      document.body.setAttribute('data-theme', 'light');
      document.documentElement.style.colorScheme = 'light';
      document.body.style.colorScheme = 'light';
    };
    
    forceLightMode();
    
    // Observer les changements de classe pour maintenir le mode clair
    const observer = new MutationObserver(() => {
      if (document.documentElement.classList.contains('dark')) {
        forceLightMode();
      }
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    // Cleanup : restaurer le thème du contexte quand on quitte la page
    return () => {
      observer.disconnect();
      
      // Restaurer le thème à partir du contexte
      const root = document.documentElement;
      const body = document.body;
      
      if (theme === 'dark') {
        root.classList.add('dark');
        body.classList.add('dark');
        root.style.colorScheme = 'dark';
        body.style.colorScheme = 'dark';
        root.setAttribute('data-theme', 'dark');
        body.setAttribute('data-theme', 'dark');
      } else {
        root.classList.remove('dark');
        body.classList.remove('dark');
        root.style.colorScheme = 'light';
        body.style.colorScheme = 'light';
        root.setAttribute('data-theme', 'light');
        body.setAttribute('data-theme', 'light');
      }
    };
  }, [theme]);

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-gray-900" style={{ overflowX: 'clip' }} data-theme="light">
      <LandingSeoHead />
      {/* Fixed Header Container */}
      <div className="fixed top-0 left-0 right-0 z-[60] flex flex-col" style={{ background: '#0B0B0C' }}>
        {/* Black Friday Banner — shown only Nov 26–30, every year */}
        {(() => {
          const now = new Date();
          const month = now.getMonth(); // 0-indexed, 10 = November
          const day = now.getDate();
          const isBlackFriday = month === 10 && day >= 26 && day <= 30;
          if (!isBlackFriday) return null;
          return (
            <div className="bg-black !text-white text-center py-4 px-4 font-bold text-lg sm:text-xl md:text-2xl flex items-center justify-center gap-2 flex-wrap">
              <span style={{ background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                {t('landing.banner.black_friday')}
              </span>
              <span className="!text-white">{t('landing.banner.black_friday_mid')}</span>
              <span style={{ background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                50%
              </span>
              <span className="!text-white">{t('landing.banner.black_friday_suffix')}</span>
            </div>
          );
        })()}

        {/* ── Bannière d'annonce : module Prop Firm ──
            Fond noir plein / py-4 / text-lg-xl-2xl, même gabarit que la bannière
            Black Friday ci-dessus. Le badge « Nouveau » est copié à l'identique
            de celui du menu (Layout.tsx, section Prop Firm de la sidebar) : anneau
            en dégradé + pastille sombre à l'intérieur — juste plus de padding. */}
        <button
          type="button"
          onClick={scrollToPropFirm}
          className="group bg-black !text-white text-center py-2 px-3 sm:py-4 sm:px-4 font-bold text-sm sm:text-base md:text-lg flex items-center justify-center gap-2 sm:gap-3 flex-wrap w-full"
          style={{ border: 'none', cursor: 'pointer' }}
        >
          <span
            className="inline-flex rounded-full"
            style={{ padding: '1px', background: 'linear-gradient(135deg, #3d2a6e 0%, #7C76FF 40%, #c45aaa 70%, #FF7AD9 100%)' }}
          >
            <span
              className="flex items-center rounded-full"
              style={{ background: '#0a0a0d', padding: '3px 8px', fontSize: '0.55rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', lineHeight: 1, color: '#fff' }}
            >
              {t('landing.common.new')}
            </span>
          </span>
          <span className="!text-white sm:hidden">{t('landing.banner.propfirm_short')}</span>
          <span className="!text-white hidden sm:inline">{t('landing.banner.propfirm_long')}</span>
          <svg
            width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className="transition-transform duration-200 ease-out group-hover:translate-x-1"
            style={{ width: '0.7em', height: '0.7em', flexShrink: 0 }}
            aria-hidden="true"
          >
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
          </svg>
        </button>

        {/* Navbar */}
        <Navbar backgroundColor="#0B0B0C" showAnimations={true} position="relative" navigationVariant="landing" isDark={true} />
      </div>

      {/* Hero Section - Premium Centered */}
      <section
        className="relative overflow-hidden"
        style={{ background: '#0B0B0C', paddingTop: 'clamp(120px, 15vw, 160px)', paddingBottom: '80px' }}
      >
        {/* ── Background: radial glow + subtle dot grid ── */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {/* Top-center radial glow — the signature premium dark-site touch */}
          <div style={{
            position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
            width: '900px', height: '600px',
            background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(109,40,217,0.18) 0%, transparent 70%)',
          }} />
        </div>

                {/* ── Centered text content ── */}
        <div className="relative z-10 text-center w-full px-4">

          {/* Badge */}
          <div
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '5px 14px 5px 10px', borderRadius: '100px',
              background: 'rgba(255,255,255,0.05)',
              marginBottom: '36px',
              animation: 'fpFadeUp 0.6s ease-out both',
            }}
          >
            <img src={atlasMainWebP} alt="Atlas" style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: 4, flexShrink: 0 }} />
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', fontWeight: 500 }}>
              {t('landing.hero.badge', { country: t('landing.country') })}
            </span>
          </div>

          {/* Headline */}
          <h1 style={{
            margin: '0 0 24px',
            fontWeight: 800,
            letterSpacing: '-0.04em',
            lineHeight: 1.05,
            textAlign: 'center',
            animation: 'fpFadeUp 0.6s ease-out 0.1s both',
          }}>
            <span style={{
              display: 'block',
              fontSize: 'clamp(1.5rem, 2.7vw, 2.9rem)',
              color: '#FFFFFF',
              marginBottom: '0.35em',
            }}>
              {/* Formulation raccourcie sur mobile, complète à partir de sm.
                  Sur mobile la ligne doit tenir sur UNE ligne : FitText part de
                  1.85rem et réduit la police jusqu'à ce que ça rentre. */}
              <FitText className="sm:hidden" maxRem={1.85}>{t('landing.hero.title_mobile')}</FitText>
              <span className="hidden sm:inline">{t('landing.hero.title_desktop')}</span>
            </span>
            {/* Mobile : une seule ligne, police ajustée depuis 1.62rem (FitText ne
                pose une taille inline qu'en dessous de sm). Desktop : le clamp via
                la classe sm:text-[...] comme avant. Le span bloc extérieur garde la
                mise en pile des lignes ; le FitText reste inline pour être mesurable. */}
            <span className="block">
              <FitText
                maxRem={1.62}
                className="sm:text-[length:clamp(1.5rem,2.7vw,2.9rem)]"
                style={{ background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
              >
                {t('landing.hero.title_gradient')}
              </FitText>
            </span>
          </h1>

          {/* Subheadline */}
          <p style={{
            color: 'rgba(255,255,255,0.46)',
            fontSize: 'clamp(1rem, 1.5vw, 1.15rem)',
            lineHeight: 1.7,
            maxWidth: '760px',
            margin: '0 auto 40px',
            animation: 'fpFadeUp 0.6s ease-out 0.18s both',
          }}>
            {/* Mobile : même promesse, formulation plus courte */}
            <span className="sm:hidden">{t('landing.hero.subtitle_mobile')}</span>
            {/* Desktop : texte complet sur DEUX lignes max — FitText part de la taille
                du <p> (le clamp ci-dessus) et ne réduit que si ça déborde. */}
            <FitText only="desktop" maxLines={2} minRem={0.9} className="hidden sm:inline">
              {t('landing.hero.subtitle')}
            </FitText>
          </p>

          {/* CTA row */}
          <div style={{
            display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap',
            marginBottom: '16px',
            animation: 'fpFadeUp 0.6s ease-out 0.26s both',
          }}>
            <button
              onClick={() => { trackEvent('cta_hero_clicked', { position: 'hero', label: 'Démarrer essai gratuit' }); navigate('/register'); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '15px 30px', borderRadius: '100px',
                background: '#FFFFFF', color: '#0B0B0C',
                fontSize: '0.95rem', fontWeight: 700,
                border: 'none', cursor: 'pointer',
                boxShadow: '0 0 0 1px rgba(255,255,255,0.12), 0 8px 48px rgba(0,0,0,0.5)',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => {
                const b = e.currentTarget as HTMLButtonElement;
                b.style.transform = 'translateY(-2px)';
                b.style.boxShadow = '0 0 0 1px rgba(255,255,255,0.18), 0 16px 56px rgba(0,0,0,0.65)';
              }}
              onMouseLeave={e => {
                const b = e.currentTarget as HTMLButtonElement;
                b.style.transform = 'translateY(0)';
                b.style.boxShadow = '0 0 0 1px rgba(255,255,255,0.12), 0 8px 48px rgba(0,0,0,0.5)';
              }}
            >
              {t('landing.common.cta_analyze_free')}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="#0B0B0C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>

          {/* Microcopy */}
          <p style={{
            color: 'rgba(255,255,255,0.42)',
            fontSize: '0.82rem',
            marginBottom: '0',
            letterSpacing: '0.01em',
            animation: 'fpFadeUp 0.6s ease-out 0.32s both',
          }}>
            {t('landing.common.free_7_days')}&nbsp;&nbsp;·&nbsp;&nbsp;{t('landing.common.instant_access')}
          </p>
        </div>

        {/* ── Social proof strip ── */}
        <div
          className="relative z-10 flex items-center justify-center gap-8 mt-12 px-6 flex-wrap"
          style={{ animation: 'fpFadeUp 0.6s ease-out 0.4s both' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.83rem', marginRight: '4px' }}>4.9/5</span>
            <FastImage src={images.trustpilot.src} srcWebP={images.trustpilot.srcWebP} alt="Trustpilot" className="h-4 w-auto" style={{ opacity: 0.7 }} />
          </div>
          <div style={{ width: '1px', height: '18px', background: 'rgba(255,255,255,0.1)' }} className="hidden sm:block" />
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'rgba(255,255,255,0.4)', fontSize: '0.83rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '10px' }}>
              {[avisTrustpilot1Png, avisTrustpilot2Png, avisTrustpilot3Png].map((avatar, index) => (
                <img
                  key={avatar}
                  src={avatar}
                  alt={t('landing.hero.alt_client')}
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    marginLeft: index === 0 ? 0 : '-10px',
                    border: '2px solid #0B0B0C',
                    boxShadow: '0 0 0 1px rgba(255,255,255,0.08)'
                  }}
                />
              ))}
            </div>
            <span style={{ color: 'rgba(255,255,255,0.72)', fontWeight: 600 }}>3 000+</span> {t('landing.hero.traders')}
          </div>
          <div style={{ width: '1px', height: '18px', background: 'rgba(255,255,255,0.1)' }} className="hidden sm:block" />
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <picture>
              <source srcSet={hugoFxLogoWebP} type="image/webp" />
              <img src={hugoFxLogo} alt="HugoFX" style={{ width: '22px', height: '22px', borderRadius: '50%', objectFit: 'cover' }} />
            </picture>
            {t('landing.hero.built_with')}
          </div>
        </div>

        {/* ── Feature badge carousel ── */}
        <div className="relative z-10" style={{
          position: 'relative',
          width: '520px',
          maxWidth: '90vw',
          margin: '24px auto 0',
          overflow: 'hidden',
          animation: 'fpFadeUp 0.6s ease-out 0.55s both',
          maskImage: 'linear-gradient(to right, transparent 0%, black 14%, black 86%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 14%, black 86%, transparent 100%)',
        }}>
          <div style={{
            display: 'flex',
            gap: '10px',
            width: 'max-content',
            animation: 'badgeMarquee 22s linear infinite',
          }}>
            {[
              ...['sync', 'atlas', 'journal', 'backtest', 'ai', 'risk', 'tags', 'screenshots'],
              ...['sync', 'atlas', 'journal', 'backtest', 'ai', 'risk', 'tags', 'screenshots'],
            ].map((badgeKey, i) => (
              <span key={i} style={{
                padding: '5px 14px',
                borderRadius: '100px',
                background: '#ffffff',
                color: '#0B0B0C',
                fontSize: '0.72rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                opacity: 0.75,
              }}>{t(`landing.hero.badges.${badgeKey}`)}</span>
            ))}
          </div>
        </div>

        {/* ── Product Screenshot — full-width app frame ── */}
        <div
          className="relative z-10 mx-auto mt-16 px-4 sm:px-8 lg:px-12"
          style={{
            maxWidth: '1200px',
            animation: 'fpFadeUp 0.8s ease-out 0.5s both',
          }}
        >
          {/* Screenshot container */}
          <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '14px' }}>
            <picture>
              <source media="(max-width: 900px)" srcSet={dashboardAfterBlackSmWebP} type="image/webp" />
              <source srcSet={dashboardAfterBlackWebP} type="image/webp" />
              <img
                src={dashboardAfterBlack}
                alt={t('landing.hero.alt_dashboard')}
                width={1400}
                height={801}
                style={{
                  width: '100%', height: 'auto', display: 'block',
                  borderRadius: '14px',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              />
            </picture>

            {/* Bottom gradient fade — merges screenshot into the stats bar below */}
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, height: '45%',
              background: 'linear-gradient(to bottom, transparent 0%, rgba(11,11,12,0.97) 100%)',
              borderRadius: '0 0 14px 14px',
              pointerEvents: 'none',
            }} />
          </div>
        </div>

        {/* ── Stats bar ── */}

        {/* ── Hero CSS ── */}
        <style>{`
          @keyframes fpFadeUp {
            from { opacity: 0; transform: translateY(20px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes fpPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%       { opacity: 0.5; transform: scale(0.75); }
          }
          @keyframes fpFloat1 {
            0%, 100% { transform: translateY(0px); }
            50%      { transform: translateY(-7px); }
          }
          @keyframes badgeFloat {
            0%, 100% { transform: translateY(0px); }
            45%      { transform: translateY(-3px); }
            75%      { transform: translateY(-1px); }
          }
          @keyframes badgeMarquee {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @keyframes fpFloat2 {
            0%, 100% { transform: translateY(0px); }
            50%      { transform: translateY(6px); }
          }
          @keyframes gradientShift {
            0%, 100% { background-position: 0% 50%; }
            50%       { background-position: 100% 50%; }
          }
          @keyframes float {
            0%, 100% { transform: translate(0, 0) scale(1); }
            33%      { transform: translate(30px, -30px) scale(1.1); }
            66%      { transform: translate(-20px, 20px) scale(0.9); }
          }
          @keyframes partnerReviewScroll {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @keyframes fadeInLeft {
            from { opacity: 0.7; transform: translateX(-5px); }
            to   { opacity: 1;   transform: translateX(0); }
          }
          @keyframes fadeInRight {
            from { opacity: 0.7; transform: translateX(5px); }
            to   { opacity: 1;   transform: translateX(0); }
          }
          .toggle-monthly-active {
            animation: fadeInLeft 0.4s ease-out;
          }
          .toggle-yearly-active {
            animation: fadeInRight 0.4s ease-out;
          }
          /* Toggle Tarifs : pastille active blanche, texte sombre (identique à /pricing). */
          .pricing-toggle-active {
            color: #0a0a0d !important;
          }
          .pricing-toggle-inactive {
            color: rgba(255, 255, 255, 0.7) !important;
          }

          /* ── Global light-mode enforcement for landing page ── */
          [data-theme="light"] { color-scheme: light !important; }
          [data-theme="light"] * { color-scheme: light !important; }
          html:not(.dark) { color-scheme: light !important; }
          html:not(.dark) * { color-scheme: light !important; }
          .dark { color-scheme: light !important; }
          .dark * { color-scheme: light !important; }
          html, body, #root { background-color: white; color: #000000; }
          [data-theme="light"] { background-color: white; color: #000000; }

          .perspective-1000 { perspective: 1000px; }
        `}</style>
      </section>

      {/* ── Game Changer Section ── */}
      <section style={{ background: '#101114', padding: '80px 0 100px', position: 'relative', overflow: 'hidden', marginTop: '-1px' }}>

        <AnimatedText delay={0} duration={700}>
        {/* Header */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24 pt-8 sm:pt-16 pb-8 sm:pb-12">
          {/* Section title — centered, same style as other dark sections */}
          <div style={{ marginBottom: '48px', textAlign: 'center' }}>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 16px' }}>{t('landing.compare.eyebrow')}</p>
            <h2 style={{ color: '#FFFFFF', fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 20px' }}>
              <span className="sm:whitespace-nowrap">{t('landing.compare.title_1')}</span>
              <br />
              <span className="sm:whitespace-nowrap" style={{ background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{t('landing.compare.title_2')}</span>
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '1rem', maxWidth: '520px', lineHeight: 1.7, margin: '0 auto' }}>
              {t('landing.compare.subtitle')}
            </p>
          </div>
        </div>
        </AnimatedText>

        <AnimatedText delay={80} duration={700}>
        {/* Tableau comparatif — 4 colonnes indépendantes, chacune sa propre carte.
            La colonne FocusPips est ainsi un bloc continu (bordure dégradée + halo)
            au lieu d'être redécoupée à chaque ligne. L'alignement horizontal vient
            des hauteurs de cellule partagées (CMP_ROW_H / CMP_HEAD_H). */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
          <div className="flex items-start gap-2 sm:gap-4">

            {/* ── Colonne 1 : libellés des fonctionnalités (pas de carte) ── */}
            <div className="flex-[1.6] sm:flex-[2] min-w-0">
              <div className={CMP_HEAD_H} />
              {CMP_ROWS.map((row, i) => (
                <div
                  key={row.row}
                  className={`${CMP_ROW_H} flex items-center pr-2 ${i === CMP_ROWS.length - 1 ? '' : 'border-b'}`}
                  style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                >
                  <h3 className="text-[12px] leading-[1.25] sm:text-base font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {t(`landing.compare.rows.${row.row}`)}
                  </h3>
                </div>
              ))}
            </div>

            {/* ── Colonne 2 : Excel / Notion — pas de carte, colonne nue ── */}
            <div className="flex-1 min-w-0">
              <div className={`${CMP_HEAD_H} flex flex-col items-center justify-center gap-1.5 px-2 border-b`} style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                <div className="flex items-center justify-center gap-3 sm:gap-4">
                  <img src={excelLogoPng} alt="" className="h-6 w-6 sm:h-8 sm:w-8 object-contain flex-shrink-0" loading="lazy" />
                  <img src={notionLogoPng} alt="" className="h-6 w-6 sm:h-8 sm:w-8 object-contain rounded flex-shrink-0" loading="lazy" />
                </div>
                <h3 className="text-[9px] leading-tight sm:text-base font-semibold text-center" style={{ color: '#ffffff' }}>{t('landing.compare.col_excel')}</h3>
              </div>
              {CMP_ROWS.map((row, i) => (
                <CmpCell key={row.row} value={row.excel} last={i === CMP_ROWS.length - 1} />
              ))}
            </div>

            {/* ── Colonne 3 : Edgewonk — pas de carte, colonne nue ── */}
            <div className="flex-1 min-w-0">
              <div className={`${CMP_HEAD_H} flex flex-col items-center justify-center gap-1.5 px-2 border-b`} style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                <img src={edgewonkLogoPng} alt="" className="h-6 w-6 sm:h-8 sm:w-8 object-contain rounded flex-shrink-0" loading="lazy" />
                <h3 className="text-[9px] leading-tight sm:text-base font-semibold text-center" style={{ color: '#ffffff' }}>{t('landing.compare.col_edgewonk')}</h3>
              </div>
              {CMP_ROWS.map((row, i) => (
                <CmpCell key={row.row} value={row.edgewonk} last={i === CMP_ROWS.length - 1} />
              ))}
            </div>

            {/* ── Colonne 4 : FocusPips — seule colonne en carte (bordure dégradée + halo), et la plus large ── */}
            <div className="flex-[1.45] min-w-0 relative">
              {/* Bordure dégradée — technique double background-clip plutôt qu'un
                  wrapper en padding : le navigateur dessine la bordure lui-même,
                  donc épaisseur rigoureusement constante y compris dans les angles
                  (les rayons intérieur/extérieur restent concentriques). Le calque
                  padding-box est opaque, ce qui empêche le dégradé animé de
                  transparaître à travers la carte. */}
              <div
                style={{
                  borderRadius: '18px',
                  border: '1.5px solid transparent',
                  backgroundImage: 'linear-gradient(#0B0B0C, #0B0B0C), linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)',
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box',
                  backgroundSize: '100% 100%, 200% 200%',
                  animation: 'gradientShift 4s ease infinite',
                  boxShadow: '0 0 60px rgba(139,92,246,0.22)',
                }}
              >
                <div
                  className="rounded-[16.5px] overflow-hidden"
                  style={{ background: 'linear-gradient(180deg, rgba(139,92,246,0.16) 0%, rgba(139,92,246,0) 55%)' }}
                >
                  <div className={`${CMP_HEAD_H} flex items-center justify-center px-2 border-b`} style={{ borderColor: 'rgba(167,139,250,0.16)' }}>
                    <img src={logoSvgWebP} alt="FocusPips" className="h-7 sm:h-9 w-auto object-contain" loading="lazy" />
                  </div>
                  {CMP_ROWS.map((row) => (
                    <CmpCell key={row.row} value={row.focus} highlight />
                  ))}
                  {/* Pied de carte */}
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 py-4">
                    {/* Mobile : icône agrandie et redressée (le SVG source pointe
                        vers le haut-droite, -45° la remet à la verticale). */}
                    <span className="block shrink-0 w-8 h-8 -rotate-45 sm:w-[22px] sm:h-[22px] sm:rotate-0">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                        <defs>
                          <linearGradient id="cmpRocketGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <animate attributeName="x1" values="0%;100%;0%" dur="4s" repeatCount="indefinite" />
                            <animate attributeName="x2" values="100%;200%;100%" dur="4s" repeatCount="indefinite" />
                            <stop offset="0%" stopColor="#8B5CF6" />
                            <stop offset="50%" stopColor="#EC4899" />
                            <stop offset="100%" stopColor="#FB923C" />
                          </linearGradient>
                        </defs>
                        <path d="M9.03429 5.96305L6.49114 8.49856C6.02369 8.9646 5.59488 9.3921 5.25624 9.77856C5.03877 10.0267 4.82145 10.2984 4.63737 10.5985L4.61259 10.5738C4.56555 10.5269 4.54201 10.5034 4.51839 10.4805C4.07636 10.0516 3.55641 9.71062 2.98636 9.47575C2.9559 9.4632 2.92498 9.45095 2.86314 9.42645L2.48449 9.27641C1.97153 9.07315 1.83482 8.41279 2.22514 8.02365C3.34535 6.90684 4.69032 5.56594 5.33941 5.29662C5.91185 5.05911 6.53023 4.98008 7.12664 5.06822C7.67311 5.14898 8.19006 5.42968 9.03429 5.96305Z" fill="url(#cmpRocketGrad)" />
                        <path d="M13.3767 19.3132C13.5816 19.5212 13.7177 19.6681 13.8408 19.8251C14.0031 20.0322 14.1483 20.2523 14.2748 20.4829C14.4172 20.7426 14.5278 21.02 14.749 21.5748C14.929 22.0265 15.5272 22.1459 15.8746 21.7995L15.9586 21.7157C17.0788 20.5988 18.4237 19.2579 18.6938 18.6108C18.9321 18.04 19.0113 17.4235 18.9229 16.8289C18.8419 16.2841 18.5605 15.7688 18.0256 14.9273L15.474 17.4713C14.9959 17.9479 14.5576 18.385 14.1612 18.7273C13.9236 18.9325 13.6637 19.1376 13.3767 19.3132Z" fill="url(#cmpRocketGrad)" />
                        <path fillRule="evenodd" clipRule="evenodd" d="M14.4467 16.3769L20.2935 10.5476C21.1356 9.70811 21.5566 9.28836 21.7783 8.75458C22.0001 8.22081 22.0001 7.62719 22.0001 6.43996V5.87277C22.0001 4.04713 22.0001 3.13431 21.4312 2.56715C20.8624 2 19.9468 2 18.1157 2H17.5468C16.356 2 15.7606 2 15.2252 2.2211C14.6898 2.4422 14.2688 2.86195 13.4268 3.70146L7.57991 9.53078C6.59599 10.5117 5.98591 11.12 5.74966 11.7075C5.67502 11.8931 5.6377 12.0767 5.6377 12.2692C5.6377 13.0713 6.2851 13.7168 7.57991 15.0077L7.75393 15.1812L9.79245 13.1123C10.0832 12.8172 10.558 12.8137 10.8531 13.1044C11.1481 13.3951 11.1516 13.87 10.8609 14.1651L8.8162 16.2403L8.95326 16.3769C10.2481 17.6679 10.8955 18.3133 11.7 18.3133C11.8777 18.3133 12.0478 18.2818 12.2189 18.2188C12.8222 17.9966 13.438 17.3826 14.4467 16.3769ZM17.1935 9.5312C16.435 10.2874 15.2053 10.2874 14.4468 9.5312C13.6883 8.775 13.6883 7.54895 14.4468 6.79274C15.2053 6.03653 16.435 6.03653 17.1935 6.79274C17.952 7.54895 17.952 8.775 17.1935 9.5312Z" fill="url(#cmpRocketGrad)" />
                      </svg>
                    </span>
                    <span className="sm:hidden text-[10px] font-semibold leading-tight text-center" style={{ color: '#ffffff' }}>
                      {t('landing.compare.footer_mobile')}
                    </span>
                    <span className="hidden sm:inline text-sm font-semibold leading-tight text-left" style={{ color: '#ffffff' }}>
                      {t('landing.compare.footer_desktop_1')}<br />{t('landing.compare.footer_desktop_2')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Footer note */}
          <p className="text-xs mt-6 text-center" style={{ color: 'rgba(255,255,255,0.15)' }}>
            {t('landing.compare.note')}
          </p>
        </div>
        </AnimatedText>
        <AnimatedText delay={140} duration={700}>
        {/* CTA — same as other dark sections */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', marginTop: '64px' }}>
          <button
            onClick={() => { trackEvent('cta_hero_clicked', { position: 'section_comparison', label: 'Commencer gratuitement' }); window.location.href = '/register'; }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '16px 40px', borderRadius: '100px', background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', color: '#fff', fontSize: '1rem', fontWeight: 700, border: 'none', cursor: 'pointer', letterSpacing: '-0.02em', boxShadow: '0 0 60px rgba(139,92,246,0.3)' }}
          >
            {t('landing.common.cta_start_free')}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.82rem', margin: 0 }}>{t('landing.compare.cta_note')}</p>
        </div>
        </AnimatedText>
      </section>


      {/* ── Partners Section ── */}
      {(() => {
        if (landingPartners === null) return null;
        const hugo = landingPartners.find(p => p.name?.toLowerCase().includes('hugo'));
        const rest = landingPartners.filter(p => p !== hugo);
        const allPartners = hugo ? [hugo, ...rest] : rest;
        if (allPartners.length === 0) return null;
        const total = allPartners.length;
        const formatSubs = (n: number) => n >= 1_000_000 ? `${(n/1_000_000).toFixed(1).replace('.0','')}M` : n >= 1_000 ? `${Math.floor(n/1_000)}k` : `${n}`;
        const mobilePartnersLoop = [...allPartners, ...allPartners, ...allPartners];
        const mobileTrackIndex = partnerMobileIndex || (total + partnerActive);
        return (
          <section style={{ background: '#0B0B0C', padding: '100px 0 110px', position: 'relative', overflow: 'hidden', marginTop: '-1px' }}>
            <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '1px', height: '1px', boxShadow: '0 0 500px 180px rgba(139,92,246,0.06)', pointerEvents: 'none' }} />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24" style={{ position: 'relative', zIndex: 1 }}>

              <AnimatedText delay={0} duration={700}>
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: '56px' }}>
                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 16px' }}>{t('landing.partners.eyebrow')}</p>
                <h2 style={{ color: '#ffffff', fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 16px' }}>
                  {t('landing.partners.title')}{' '}
                  <span style={{ background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{t('landing.partners.title_gradient')}</span>
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '1rem', maxWidth: '480px', margin: '0 auto', lineHeight: 1.7 }}>
                  {t('landing.partners.subtitle')}
                </p>
              </div>
              </AnimatedText>

              <AnimatedText delay={90} duration={700}>
              {/* ── DESKTOP: carousel ── */}
              <div className="hidden sm:block">

                {/* Carousel */}
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', height: '520px' }}>
                {allPartners.map((p, i) => {
                  const isActive = i === partnerActive;
                  const reach = p.subscribers ? Object.values(p.subscribers).reduce((s,v)=>s+v,0) : 0;
                  const isHugo = p === hugo;
                  return (
                    <div
                      key={p.id}
                      onMouseEnter={() => { if (partnerTimerRef.current) clearInterval(partnerTimerRef.current); setPartnerActive(i); }}
                      onMouseLeave={() => partnerGoTo(partnerActive)}
                      onClick={() => partnerGoTo(i)}
                      style={{ flex: '1', height: isActive ? '520px' : '380px', transition: 'height 0.65s cubic-bezier(0.4,0,0.2,1)', position: 'relative', cursor: 'pointer' }}
                    >
                      {/* Hugo badge: straddling top edge, visible only when active */}
                      {isHugo && (
                        <div style={{ position: 'absolute', top: '-14px', left: 0, right: 0, display: 'flex', justifyContent: 'center', pointerEvents: 'none', zIndex: 10, opacity: isActive ? 1 : 0, transition: 'opacity 0.35s ease' }}>
                          <span style={{ background: '#0B0B0C', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '100px', padding: '5px 16px', color: '#ffffff', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{t('landing.partners.main_partner')}</span>
                        </div>
                      )}

                      {/* Inner clip container */}
                      <div style={{ position: 'absolute', inset: 0, borderRadius: '18px', overflow: 'hidden', border: '1px solid #0B0B0C' }}>
                      {(p.localPhoto || p.photoURL) ? (
                        <picture>
                          {p.localPhotoSmWebP && <source media="(max-width: 900px)" srcSet={p.localPhotoSmWebP} type="image/webp" />}
                          {p.localPhotoWebP && <source srcSet={p.localPhotoWebP} type="image/webp" />}
                          <img src={p.localPhoto || p.photoURL || ''} alt={p.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                        </picture>
                      ) : (
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', fontWeight: 900, color: '#fff' }}>
                          {(p.name||'?')[0].toUpperCase()}
                        </div>
                      )}
                      <div style={{ position: 'absolute', inset: 0, background: isActive ? 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.35) 55%, transparent 100%)' : 'rgba(0,0,0,0.6)', transition: 'background 0.65s ease' }} />

                      {/* Collapsed: name horizontal bottom */}
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '16px', opacity: isActive ? 0 : 1, transition: 'opacity 0.35s ease', pointerEvents: 'none' }}>
                        <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.03em', textAlign: 'center', margin: 0 }}>{p.name}</p>
                      </div>

                      {/* Expanded: full info */}
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '32px 28px', opacity: isActive ? 1 : 0, transform: isActive ? 'translateY(0)' : 'translateY(10px)', transition: 'opacity 0.45s ease 0.1s, transform 0.45s ease 0.1s', pointerEvents: 'none' }}>
                        <h3 style={{ color: '#ffffff', fontSize: 'clamp(1.3rem, 2vw, 1.7rem)', fontWeight: 800, margin: '0 0 8px', letterSpacing: '-0.03em', lineHeight: 1.1 }}>{p.name}</h3>
                        {p.bio && <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', lineHeight: 1.6, margin: '0 0 12px', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.bio}</p>}
                        {reach > 0 && <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', margin: 0 }}><span style={{ color: '#ffffff', fontWeight: 800, fontSize: '1.4rem', letterSpacing: '-0.02em' }}>{formatSubs(reach)}</span> {t('landing.partners.subscribers')}</p>}
                      </div>
                      </div>
                    </div>
                  );
                })}
                </div>

              </div>

              {/* ── MOBILE: sliding track ── */}
              <div className="sm:hidden" style={{ overflow: 'hidden', margin: '0 -16px' }}>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  transform: `translateX(calc(48px - ${mobileTrackIndex} * (100vw - 84px)))`,
                  transition: partnerMobileTransitionEnabled ? 'transform 0.55s cubic-bezier(0.4,0,0.2,1)' : 'none',
                  willChange: 'transform',
                }}>
                  {mobilePartnersLoop.map((p, i) => {
                    const actualIndex = i % total;
                    const isCardActive = i === mobileTrackIndex;
                    const reach = p.subscribers ? Object.values(p.subscribers).reduce((s,v)=>s+v,0) : 0;
                    const isHugo = p === hugo;
                    return (
                      <div
                        key={`${p.id}-${i}`}
                        onClick={() => partnerGoTo(actualIndex)}
                        style={{
                          minWidth: 'calc(100vw - 96px)',
                          borderRadius: '16px',
                          overflow: 'hidden',
                          border: isCardActive ? '1px solid rgba(167,139,250,0.2)' : '1px solid rgba(255,255,255,0.06)',
                          background: '#141416',
                          opacity: isCardActive ? 1 : 0.4,
                          transform: isCardActive ? 'scale(1)' : 'scale(0.96)',
                          transition: 'opacity 0.55s ease, transform 0.55s ease',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <div style={{ width: '100%', aspectRatio: '3/3.7', position: 'relative', overflow: 'hidden', background: '#141416' }}>
                          {(p.localPhoto || p.photoURL) ? (
                            <picture>
                              {p.localPhotoSmWebP && <source media="(max-width: 900px)" srcSet={p.localPhotoSmWebP} type="image/webp" />}
                              {p.localPhotoWebP && <source srcSet={p.localPhotoWebP} type="image/webp" />}
                              <img src={p.localPhoto || p.photoURL || ''} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
                            </picture>
                          ) : (
                            <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',background:'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)',fontSize:'3rem',fontWeight:900,color:'#fff' }}>{(p.name||'?')[0].toUpperCase()}</div>
                          )}
                          <div style={{ position:'absolute',bottom:0,left:0,right:0,height:'50%',background:'linear-gradient(to top,rgba(0,0,0,0.8),transparent)' }} />
                          {isHugo && (
                            <div style={{ position:'absolute',top:'14px',left:0,right:0,display:'flex',justifyContent:'center',pointerEvents:'none' }}>
                              <span style={{ background:'rgba(0,0,0,0.5)',borderRadius:'100px',padding:'5px 14px',color:'#ffffff',fontSize:'0.62rem',fontWeight:700,letterSpacing:'0.1em',textTransform:'uppercase' }}>{t('landing.partners.main_partner')}</span>
                            </div>
                          )}
                        </div>
                        <div style={{ background:'#141416',padding:'16px 16px 18px',flex:1 }}>
                          <h3 style={{ color:'#ffffff',fontSize:'1.05rem',fontWeight:800,margin:'0 0 6px',letterSpacing:'-0.02em',lineHeight:1.1 }}>{p.name}</h3>
                          {p.bio && <p style={{ color:'rgba(255,255,255,0.45)',fontSize:'0.8rem',lineHeight:1.55,margin:'0 0 10px' }}>{p.bio}</p>}
                          {reach > 0 && <p style={{ color:'rgba(255,255,255,0.5)',fontSize:'0.78rem',margin:0 }}><span style={{ color:'#ffffff',fontWeight:800,fontSize:'1.25rem',letterSpacing:'-0.02em' }}>{formatSubs(reach)}</span> {t('landing.partners.subscribers')}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              </AnimatedText>

              <AnimatedText delay={160} duration={700}>
              {/* ── Social proof + infinite review carousel ── */}
              <div style={{ marginTop: '80px', textAlign: 'center' }}>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', fontWeight: 500, margin: '0 0 16px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t('landing.partners.approved')}</p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '6px' }}>
                  <FastImage src={images.trustpilot.src} srcWebP={images.trustpilot.srcWebP} alt="Trustpilot" className="h-5 w-auto" />
                  <span style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 700 }}>{t('landing.partners.rating')}</span>
                </div>
                <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.83rem', margin: '0 0 0' }}>{t('landing.partners.based_on')}</p>

                {/* Infinite marquee of review cards */}
                <div style={{
                  position: 'relative',
                  width: '100%',
                  overflow: 'hidden',
                  marginTop: '56px',
                  maskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
                }}>
                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    width: 'max-content',
                    animation: 'partnerReviewScroll 30s linear infinite',
                  }}>
                    {[
                      { img: avisTrustpilot1Png, name: 'Max Mntl', key: 'r1' },
                      { img: avisTrustpilot2Png, name: 'Melvyn Bouyer', key: 'r2' },
                      { img: avisTrustpilot3Png, name: 'Charles Benoit', key: 'r3' },
                      { img: avisTrustpilot4Png, name: 'Vainina Colombel', key: 'r4' },
                      { img: avisTrustpilot5Png, name: 'Cyril', key: 'r5' },
                      { img: avisTrustpilot6Png, name: 'Johann Valerius', key: 'r6' },
                      { img: avisTrustpilot1Png, name: 'Max Mntl', key: 'r1' },
                      { img: avisTrustpilot2Png, name: 'Melvyn Bouyer', key: 'r2' },
                      { img: avisTrustpilot3Png, name: 'Charles Benoit', key: 'r3' },
                      { img: avisTrustpilot4Png, name: 'Vainina Colombel', key: 'r4' },
                      { img: avisTrustpilot5Png, name: 'Cyril', key: 'r5' },
                      { img: avisTrustpilot6Png, name: 'Johann Valerius', key: 'r6' },
                    ].map((review, i) => (
                      <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '16px 18px', width: '240px', flexShrink: 0, textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            <img src={review.img} alt={review.name} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', bottom: -2, right: -2, width: 12, height: 12 }}><path fillRule="evenodd" clipRule="evenodd" d="M9.5924 3.20027C9.34888 3.4078 9.22711 3.51158 9.09706 3.59874C8.79896 3.79854 8.46417 3.93721 8.1121 4.00672C7.95851 4.03705 7.79903 4.04977 7.48008 4.07522C6.6787 4.13918 6.278 4.17115 5.94371 4.28923C5.17051 4.56233 4.56233 5.17051 4.28923 5.94371C4.17115 6.278 4.13918 6.6787 4.07522 7.48008C4.04977 7.79903 4.03705 7.95851 4.00672 8.1121C3.93721 8.46417 3.79854 8.79896 3.59874 9.09706C3.51158 9.22711 3.40781 9.34887 3.20027 9.5924C2.67883 10.2043 2.4181 10.5102 2.26522 10.8301C1.91159 11.57 1.91159 12.43 2.26522 13.1699C2.41811 13.4898 2.67883 13.7957 3.20027 14.4076C3.40778 14.6511 3.51158 14.7729 3.59874 14.9029C3.79854 15.201 3.93721 15.5358 4.00672 15.8879C4.03705 16.0415 4.04977 16.201 4.07522 16.5199C4.13918 17.3213 4.17115 17.722 4.28923 18.0563C4.56233 18.8295 5.17051 19.4377 5.94371 19.7108C6.278 19.8288 6.6787 19.8608 7.48008 19.9248C7.79903 19.9502 7.95851 19.963 8.1121 19.9933C8.46417 20.0628 8.79896 20.2015 9.09706 20.4013C9.22711 20.4884 9.34887 20.5922 9.5924 20.7997C10.2043 21.3212 10.5102 21.5819 10.8301 21.7348C11.57 22.0884 12.43 22.0884 13.1699 21.7348C13.4898 21.5819 13.7957 21.3212 14.4076 20.7997C14.6511 20.5922 14.7729 20.4884 14.9029 20.4013C15.201 20.2015 15.5358 20.0628 15.8879 19.9933C16.0415 19.963 16.201 19.9502 16.5199 19.9248C17.3213 19.8608 17.722 19.8288 18.0563 19.7108C18.8295 19.4377 19.4377 18.8295 19.7108 18.0563C19.8288 17.722 19.8608 17.3213 19.9248 16.5199C19.9502 16.201 19.963 16.0415 19.9933 15.8879C20.0628 15.5358 20.2015 15.201 20.4013 14.9029C20.4884 14.7729 20.5922 14.6511 20.7997 14.4076C21.3212 13.7957 21.5819 13.4898 21.7348 13.1699C22.0884 12.43 22.0884 11.57 21.7348 10.8301C21.5819 10.5102 21.3212 10.2043 20.7997 9.5924C20.5922 9.34887 20.4884 9.22711 20.4013 9.09706C20.2015 8.79896 20.0628 8.46417 19.9933 8.1121C19.963 7.95851 19.9502 7.79903 19.9248 7.48008C19.8608 6.6787 19.8288 6.278 19.7108 5.94371C19.4377 5.17051 18.8295 4.56233 18.0563 4.28923C17.722 4.17115 17.3213 4.13918 16.5199 4.07522C16.201 4.04977 16.0415 4.03705 15.8879 4.00672C15.5358 3.93721 15.201 3.79854 14.9029 3.59874C14.7729 3.51158 14.6511 3.40781 14.4076 3.20027C13.7957 2.67883 13.4898 2.41811 13.1699 2.26522C12.43 1.91159 11.57 1.91159 10.8301 2.26522C10.5102 2.4181 10.2043 2.67883 9.5924 3.20027ZM16.3735 9.86314C16.6913 9.5453 16.6913 9.03 16.3735 8.71216C16.0557 8.39433 15.5403 8.39433 15.2225 8.71216L10.3723 13.5624L8.77746 11.9676C8.45963 11.6498 7.94432 11.6498 7.62649 11.9676C7.30866 12.2854 7.30866 12.8007 7.62649 13.1186L9.79678 15.2889C10.1146 15.6067 10.6299 15.6067 10.9478 15.2889L16.3735 9.86314Z" fill="#00B67A"/></svg>
                          </div>
                          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem', fontWeight: 600 }}>{review.name}</span>
                        </div>
                        <p style={{ color: '#fff', fontSize: '0.78rem', fontWeight: 700, margin: '0 0 6px', lineHeight: 1.3 }}>{t(`landing.partners.reviews.${review.key}.title`)}</p>
                        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', lineHeight: 1.55, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' }}>{t(`landing.partners.reviews.${review.key}.text`)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Disclaimer témoignages */}
              <p className="text-xs mt-6 text-center" style={{ color: 'rgba(255,255,255,0.15)' }}>
                {t('landing.partners.disclaimer')}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', marginTop: '56px' }}>
                <button
                  onClick={() => { trackEvent('cta_hero_clicked', { position: 'section_testimonials', label: 'Analyser mes trades gratuitement' }); window.location.href = '/register'; }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: 'clamp(14px, 3.5vw, 16px) clamp(20px, 7vw, 40px)', borderRadius: '100px', background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', color: '#fff', fontSize: 'clamp(0.82rem, 4vw, 1rem)', fontWeight: 700, border: 'none', cursor: 'pointer', letterSpacing: '-0.02em', boxShadow: '0 0 60px rgba(139,92,246,0.3)' }}
                >
                  {t('landing.common.cta_analyze_free')}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.78rem', margin: 0 }}>{t('landing.common.instant_access')}</p>
              </div>
              </AnimatedText>
            </div>
          </section>
        );
      })()}



            {/* ── Before/After Section ── */}
      <section style={{ background: '#101114', position: 'relative', marginTop: '-1px' }}>
        {(() => {
          const sectionRef  = React.useRef<HTMLDivElement>(null);
          const glowGrnRef  = React.useRef<HTMLDivElement>(null);
          const block1Ref   = React.useRef<HTMLDivElement>(null);
          const block2Ref   = React.useRef<HTMLDivElement>(null);
          const block3Ref   = React.useRef<HTMLDivElement>(null);
          const ctaRef      = React.useRef<HTMLDivElement>(null);

          React.useEffect(() => {
            const section = sectionRef.current;
            if (!section) return;
            let triggered = false;
            const observer = new IntersectionObserver(([e]) => {
              if (e.isIntersecting && !triggered) {
                triggered = true;
                if (glowGrnRef.current)  glowGrnRef.current.style.opacity = '1';
                // Bloc 1
                setTimeout(() => {
                  if (block1Ref.current) { block1Ref.current.style.opacity = '1'; block1Ref.current.style.transform = 'translateY(0)'; }
                }, 0);
                // Bloc 2
                setTimeout(() => {
                  if (block2Ref.current) { block2Ref.current.style.opacity = '1'; block2Ref.current.style.transform = 'translateY(0)'; }
                }, 300);
                // Bloc 3
                setTimeout(() => {
                  if (block3Ref.current) { block3Ref.current.style.opacity = '1'; block3Ref.current.style.transform = 'translateY(0)'; }
                }, 600);
                // CTA
                setTimeout(() => {
                  if (ctaRef.current) { ctaRef.current.style.opacity = '1'; ctaRef.current.style.transform = 'translateY(0)'; }
                }, 900);
              }
            }, { threshold: 0.2 });
            observer.observe(section);
            return () => observer.disconnect();
          }, []);

          const imgStyle = { width: '100%', display: 'block', borderRadius: '18px', boxShadow: '0 30px 70px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.06)' };
          const blockBaseDesktop = 'ba-block';

          return (
            <div ref={sectionRef} style={{ position: 'relative' }}>

              {/* Lueur verte */}
              <div ref={glowGrnRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '80vh', minHeight: '540px', opacity: 0, pointerEvents: 'none', transition: 'opacity 1.8s ease', zIndex: 0 }}>
                <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 55% at 50% 50%, rgba(34,197,94,0.18) 0%, transparent 70%)' }} />
              </div>

              {/* Titre */}
              <div style={{ textAlign: 'center', padding: '100px 24px 64px' }}>
                <p style={{ color: 'rgba(255,255,255,0.28)', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', margin: '0 0 12px' }}>{t('landing.transformation.eyebrow')}</p>
                <h2 style={{ color: '#FFFFFF', fontSize: 'clamp(2rem, 3.5vw, 2.9rem)', fontWeight: 800, letterSpacing: '-0.04em', margin: 0, lineHeight: 1.1 }}>
                  {t('landing.transformation.title')}{' '}
                  <span style={{ background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{t('landing.transformation.title_gradient')}</span>
                </h2>
              </div>

              {/* ── Blocs features ── */}
              <div style={{ position: 'relative', zIndex: 2, maxWidth: '1280px', margin: '0 auto' }}>
                <style>{`.ba-block{display:flex;align-items:center;gap:64px;padding:0 48px}.ba-block>div{flex:1 1 0;min-width:0}@media(max-width:900px){.ba-block{flex-direction:column!important;gap:32px;padding:0 20px}}`}</style>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '80px', padding: '40px 0 80px' }}>

                  {/* Bloc 1 : Dashboard GAUCHE, texte DROITE */}
                  <div ref={block1Ref} style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }} className={blockBaseDesktop}>
                    <div>
                      <picture>
                        <source media="(max-width: 900px)" srcSet={dashboardTransfoSmWebP} type="image/webp" />
                        <source srcSet={dashboardTransfoWebP} type="image/webp" />
                        <img src={dashboardTransfoPng} alt={t('landing.transformation.dashboard.alt')} style={imgStyle} />
                      </picture>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(167,139,250,0.8)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', margin: '0 0 16px' }}>{t('landing.transformation.dashboard.eyebrow')}</p>
                      <h3 style={{ color: '#FFFFFF', fontSize: 'clamp(1.4rem, 2.2vw, 2rem)', fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 16px', lineHeight: 1.15 }}>{t('landing.transformation.dashboard.title')}</h3>
                      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem', lineHeight: 1.7, margin: '0 0 28px' }}>{t('landing.transformation.dashboard.subtitle')}</p>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {['b1', 'b2', 'b3'].map((b, i) => (
                          <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', fontWeight: 500 }}>
                            <CheckCircle2 style={{ width: 18, height: 18, flexShrink: 0, color: '#8B5CF6' }} />
                            {t(`landing.transformation.dashboard.${b}`)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Bloc 2 : texte GAUCHE, Journal DROITE */}
                  <div ref={block2Ref} style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }} className="ba-block ba-block--reverse">
                    <style>{`.ba-block--reverse{flex-direction:row-reverse}@media(max-width:900px){.ba-block--reverse{flex-direction:column!important}}`}</style>
                    <div>
                      <picture>
                        <source media="(max-width: 900px)" srcSet={journalTransfoSmWebP} type="image/webp" />
                        <source srcSet={journalTransfoWebP} type="image/webp" />
                        <img src={journalTransfoPng} alt={t('landing.transformation.journal.alt')} style={imgStyle} />
                      </picture>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(167,139,250,0.8)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', margin: '0 0 16px' }}>{t('landing.transformation.journal.eyebrow')}</p>
                      <h3 style={{ color: '#FFFFFF', fontSize: 'clamp(1.4rem, 2.2vw, 2rem)', fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 16px', lineHeight: 1.15 }}>{t('landing.transformation.journal.title')}</h3>
                      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem', lineHeight: 1.7, margin: '0 0 28px' }}>{t('landing.transformation.journal.subtitle')}</p>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {['b1', 'b2', 'b3'].map((b, i) => (
                          <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', fontWeight: 500 }}>
                            <CheckCircle2 style={{ width: 18, height: 18, flexShrink: 0, color: '#8B5CF6' }} />
                            {t(`landing.transformation.journal.${b}`)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Bloc 3 : Plans GAUCHE, texte DROITE */}
                  <div ref={block3Ref} style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }} className={blockBaseDesktop}>
                    <div>
                      <picture>
                        <source media="(max-width: 900px)" srcSet={plansTransfoSmWebP} type="image/webp" />
                        <source srcSet={plansTransfoWebP} type="image/webp" />
                        <img src={plansTransfoPng} alt={t('landing.transformation.plans.alt')} style={imgStyle} />
                      </picture>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(167,139,250,0.8)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', margin: '0 0 16px' }}>{t('landing.transformation.plans.eyebrow')}</p>
                      <h3 style={{ color: '#FFFFFF', fontSize: 'clamp(1.4rem, 2.2vw, 2rem)', fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 16px', lineHeight: 1.15 }}>{t('landing.transformation.plans.title')}</h3>
                      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem', lineHeight: 1.7, margin: '0 0 28px' }}>{t('landing.transformation.plans.subtitle')}</p>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {['b1', 'b2', 'b3'].map((b, i) => (
                          <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', fontWeight: 500 }}>
                            <CheckCircle2 style={{ width: 18, height: 18, flexShrink: 0, color: '#8B5CF6' }} />
                            {t(`landing.transformation.plans.${b}`)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                </div>
              </div>
              {/* fin container unifié */}

              {/* CTA */}
              <div ref={ctaRef} style={{ textAlign: 'center', padding: '48px 0 64px', opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.85s ease, transform 0.85s ease' }}>
                <button onClick={() => { trackEvent('cta_hero_clicked', { position: 'section_pricing', label: 'Commencer gratuitement' }); window.location.href = '/register'; }} style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '15px 36px', borderRadius: '9999px', background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', color: '#fff', fontSize: '1rem', fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 0 50px rgba(139,92,246,0.35)', whiteSpace: 'nowrap' }}>
                  {t('landing.common.cta_start_free')}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.78rem', margin: '14px 0 0' }}>{t('landing.common.instant_access')}</p>
              </div>
            </div>
          );
        })()}
      </section>

      {/* ── Prop Firm Section ──
          Placée juste avant le pricing : c'est l'argument le plus différenciant
          de la page, et celui qui justifie le prix (un challenge coûte 10× un
          mois d'abonnement). Le visiteur arrive donc sur les tarifs avec la
          raison de payer déjà en tête. */}
      <section
        id="propfirm"
        style={{ background: '#0B0B0C', padding: '100px 0 110px', position: 'relative', overflow: 'hidden', marginTop: '-1px', borderBottom: '1px solid rgba(255,255,255,0.06)', scrollMarginTop: '110px' }}
      >
        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '1px', height: '1px', boxShadow: '0 0 520px 180px rgba(139,92,246,0.09)', pointerEvents: 'none' }} />
        <style>{`
          @keyframes pfMarquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
          .pf-block{display:flex;align-items:center;gap:64px;padding:0 48px}
          .pf-block>div{flex:1 1 0;min-width:0}
          .pf-block--rev{flex-direction:row-reverse}
          @media(max-width:900px){.pf-block,.pf-block--rev{flex-direction:column!important;gap:32px;padding:0 20px}}
        `}</style>

        {(() => {
          // Lueur verte identique à la section Before/After : un fondu déclenché
          // au scroll (IntersectionObserver), pas une lueur statique.
          const pfSectionRef = React.useRef<HTMLDivElement>(null);
          const pfGlowRef = React.useRef<HTMLDivElement>(null);

          React.useEffect(() => {
            const el = pfSectionRef.current;
            if (!el) return;
            let triggered = false;
            const observer = new IntersectionObserver(([entry]) => {
              if (entry.isIntersecting && !triggered) {
                triggered = true;
                if (pfGlowRef.current) pfGlowRef.current.style.opacity = '1';
              }
            }, { threshold: 0.2 });
            observer.observe(el);
            return () => observer.disconnect();
          }, []);

          return (
        <div ref={pfSectionRef} style={{ maxWidth: '1280px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div ref={pfGlowRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '80vh', minHeight: '540px', opacity: 0, pointerEvents: 'none', transition: 'opacity 1.8s ease', zIndex: 0 }}>
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 55% at 50% 50%, rgba(34,197,94,0.16) 0%, transparent 70%)' }} />
          </div>

          {/* Header */}
          <AnimatedText delay={0} duration={700}>
            <div style={{ textAlign: 'center', marginBottom: '48px', padding: '0 24px' }}>
              <p style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 20px' }}>
                {/* Même badge que la bannière : anneau en dégradé + pastille sombre,
                    copié du menu (Layout.tsx, section Prop Firm de la sidebar). */}
                <span className="inline-flex rounded-full" style={{ padding: '2px', background: 'linear-gradient(135deg, #3d2a6e 0%, #7C76FF 40%, #c45aaa 70%, #FF7AD9 100%)' }}>
                  <span className="flex items-center rounded-full" style={{ background: '#0a0a0d', padding: '3px 9px', fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.1em', lineHeight: 1, color: '#fff' }}>
                    {t('landing.common.new')}
                  </span>
                </span>
                {t('landing.propfirm.eyebrow')}
              </p>
              <h2 style={{ color: '#FFFFFF', fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 20px' }}>
                {t('landing.propfirm.title_1')}<br />
                <span style={{ background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{t('landing.propfirm.title_2')}</span>
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '1rem', maxWidth: '480px', margin: '0 auto', lineHeight: 1.7 }}>
                {t('landing.propfirm.subtitle')}
              </p>
            </div>
          </AnimatedText>

          {/* Cockpit animé — remplace une capture figée : les jauges se
              remplissent et le compte à rebours tourne pour de vrai. */}
          <div style={{ position: 'relative', zIndex: 2, padding: '0 24px', marginBottom: '72px' }}>
            <PropFirmCockpitMock />
          </div>

          {/* Marquee des prop firms suivies */}
          <AnimatedText delay={60} duration={700}>
            <div style={{ position: 'relative', zIndex: 2, marginBottom: '96px' }}>
              <p style={{ textAlign: 'center', color: '#FFFFFF', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 32px', padding: '0 24px' }}>
                {t('landing.propfirm.marquee')}
              </p>
              <PropFirmMarquee />
            </div>
          </AnimatedText>

          <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', gap: '80px' }}>

            {/* ── Bloc 1 : Capture GAUCHE · Copy DROITE ── */}
            <AnimatedText delay={80} duration={700}>
              <div className="pf-block">
                <div>
                  <picture>
                    <source srcSet={propfirmReglesWebP} type="image/webp" />
                    <img src={propfirmReglesPng} alt={t('landing.propfirm.rules.alt')} style={{ width: '100%', display: 'block', borderRadius: '18px', boxShadow: '0 30px 70px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.06)' }} />
                  </picture>
                </div>
                <div>
                  <p style={{ color: 'rgba(167,139,250,0.8)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', margin: '0 0 16px' }}>{t('landing.propfirm.rules.eyebrow')}</p>
                  <h3 style={{ color: '#FFFFFF', fontSize: 'clamp(1.4rem, 2.2vw, 2rem)', fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 24px', lineHeight: 1.15 }}>
                    {t('landing.propfirm.rules.title')}
                  </h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {[
                      'b1', 'b2', 'b3',
                    ].map((b, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', fontWeight: 500, lineHeight: 1.5 }}>
                        <CheckCircle2 style={{ width: 18, height: 18, flexShrink: 0, color: '#8B5CF6', marginTop: 2 }} />
                        {t(`landing.propfirm.rules.${b}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </AnimatedText>

            {/* ── Bloc 2 : Copy GAUCHE · Capture DROITE ── */}
            <AnimatedText delay={120} duration={700}>
              <div className="pf-block pf-block--rev">
                <div>
                  <picture>
                    <source srcSet={propfirmSimulateurWebP} type="image/webp" />
                    <img src={propfirmSimulateurPng} alt={t('landing.propfirm.simulator.alt')} style={{ width: '100%', display: 'block', borderRadius: '18px', boxShadow: '0 30px 70px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.06)' }} />
                  </picture>
                </div>
                <div>
                  <p style={{ color: 'rgba(167,139,250,0.8)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', margin: '0 0 16px' }}>{t('landing.propfirm.simulator.eyebrow')}</p>
                  <h3 style={{ color: '#FFFFFF', fontSize: 'clamp(1.4rem, 2.2vw, 2rem)', fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 24px', lineHeight: 1.15 }}>
                    {t('landing.propfirm.simulator.title')}
                  </h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {[
                      'b1', 'b2', 'b3',
                    ].map((b, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', fontWeight: 500, lineHeight: 1.5 }}>
                        <CheckCircle2 style={{ width: 18, height: 18, flexShrink: 0, color: '#8B5CF6', marginTop: 2 }} />
                        {t(`landing.propfirm.simulator.${b}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </AnimatedText>

            {/* ── Bloc 3 : Capture GAUCHE · Copy DROITE ── */}
            <AnimatedText delay={160} duration={700}>
              <div className="pf-block">
                <div>
                  <picture>
                    <source srcSet={propfirmChallengesWebP} type="image/webp" />
                    <img src={propfirmChallengesPng} alt={t('landing.propfirm.challenges.alt')} style={{ width: '100%', display: 'block', borderRadius: '18px', boxShadow: '0 30px 70px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.06)' }} />
                  </picture>
                </div>
                <div>
                  <p style={{ color: 'rgba(167,139,250,0.8)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', margin: '0 0 16px' }}>{t('landing.propfirm.challenges.eyebrow')}</p>
                  <h3 style={{ color: '#FFFFFF', fontSize: 'clamp(1.4rem, 2.2vw, 2rem)', fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 24px', lineHeight: 1.15 }}>
                    {t('landing.propfirm.challenges.title')}
                  </h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {[
                      'b1', 'b2', 'b3',
                    ].map((b, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', fontWeight: 500, lineHeight: 1.5 }}>
                        <CheckCircle2 style={{ width: 18, height: 18, flexShrink: 0, color: '#8B5CF6', marginTop: 2 }} />
                        {t(`landing.propfirm.challenges.${b}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </AnimatedText>
          </div>

          {/* CTA */}
          <AnimatedText delay={200} duration={700}>
            <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', marginTop: '80px', padding: '0 24px' }}>
              <button
                onClick={() => { trackEvent('cta_hero_clicked', { position: 'section_propfirm', label: 'Suivre mon challenge gratuitement' }); window.location.href = '/register'; }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: 'clamp(14px, 3.5vw, 16px) clamp(20px, 7vw, 40px)', borderRadius: '100px', background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', color: '#fff', fontSize: 'clamp(0.82rem, 4vw, 1rem)', fontWeight: 700, border: 'none', cursor: 'pointer', letterSpacing: '-0.02em', boxShadow: '0 0 60px rgba(139,92,246,0.3)' }}
              >
                {t('landing.propfirm.cta')}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.82rem', margin: 0 }}>{t('landing.propfirm.cta_note')}</p>
            </div>
          </AnimatedText>
        </div>
          );
        })()}
      </section>

            {/* ── Pricing Section ── */}
      <section id="pricing" style={{ background: '#101114', padding: '100px 24px 120px', position: 'relative', overflow: 'hidden', marginTop: '-1px' }}>
        {/* Ambient glow */}
        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '1px', height: '1px', boxShadow: '0 0 500px 180px rgba(139,92,246,0.07)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 1 }}>

          <AnimatedText delay={0} duration={700}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 20px' }}>{t('landing.pricing.eyebrow')}</p>
            <h2 style={{ color: '#FFFFFF', fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 20px' }}>
              <span className="sm:whitespace-nowrap">{t('landing.pricing.title_1')}</span>
              <br />
              <span className="sm:whitespace-nowrap" style={{ background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{t('landing.pricing.title_2')}</span>
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '1rem', maxWidth: '480px', margin: '0 auto', lineHeight: 1.7, fontWeight: 400 }}>
              {t('landing.pricing.subtitle')}
            </p>
          </div>
          </AnimatedText>

          <AnimatedText delay={90} duration={700}>
          {/* Billing Toggle — identique à /pricing */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '48px' }}>
            <div className="relative inline-flex items-center rounded-full p-1.5" style={{ backgroundColor: '#1a1a24' }}>
              <button
                type="button"
                className={`relative px-7 py-2.5 text-sm whitespace-nowrap rounded-full focus:outline-none ${
                  billingInterval === 'monthly'
                    ? 'pricing-toggle-active toggle-monthly-active font-bold'
                    : 'pricing-toggle-inactive font-medium'
                }`}
                style={{
                  border: 'none',
                  outline: 'none',
                  backgroundColor: billingInterval === 'monthly' ? '#ffffff' : 'transparent'
                }}
                onClick={() => setBillingInterval('monthly')}
              >
                {t('landing.pricing.monthly')}
              </button>
              <button
                type="button"
                className={`relative px-7 py-2.5 text-sm whitespace-nowrap rounded-full focus:outline-none ${
                  billingInterval === 'yearly'
                    ? 'pricing-toggle-active toggle-yearly-active font-bold'
                    : 'pricing-toggle-inactive font-medium'
                }`}
                style={{
                  border: 'none',
                  outline: 'none',
                  backgroundColor: billingInterval === 'yearly' ? '#ffffff' : 'transparent'
                }}
                onClick={() => setBillingInterval('yearly')}
              >
                {t('landing.pricing.yearly')}
              </button>
            </div>
          </div>
          </AnimatedText>

          <AnimatedText delay={160} duration={700}>
            {/*
              La carte Premium détaillée (copy française en dur, cf. /pricing) n'existe
              qu'en français. Les 5 autres langues de cette même page (routées via
              /en /es /it /de /pt) gardent PricingPlanCards, entièrement i18n.
            */}
            {i18n.language === 'fr' ? (
              <div
                className="mt-6 sm:mt-8 pb-16 sm:pb-24 grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto lg:items-center"
                style={{ position: 'relative', zIndex: 1 }}
              >
                <PricingPlanCards
                  basicOnly
                  basicCtaPlacement="top"
                  billingInterval={billingInterval}
                  loadingUpgrade={loadingUpgrade}
                  basicCtaLabel={ctaState.basicLabelKey ? t(ctaState.basicLabelKey) : ''}
                  premiumCtaLabel={ctaState.premiumLabelKey ? t(ctaState.premiumLabelKey) : ''}
                  onBasicClick={handleLandingBasicCta}
                  onPremiumClick={handleLandingPremiumCta}
                  basicDisabled={ctaState.basicDisabled}
                  premiumDisabled={ctaState.premiumDisabled}
                  hideBasicCta={ctaState.hideBasicCta}
                />

                <PricingPremiumOfferCard
                  billingInterval={billingInterval}
                  ctaLabel={ctaState.premiumLabelKey ? t(ctaState.premiumLabelKey) : ''}
                  onCtaClick={handleLandingPremiumCta}
                  ctaDisabled={ctaState.premiumDisabled}
                  loading={loadingUpgrade}
                  showSeeAllLink={false}
                />
              </div>
            ) : (
              <PricingPlanCards
                billingInterval={billingInterval}
                loadingUpgrade={loadingUpgrade}
                basicCtaLabel={ctaState.basicLabelKey ? t(ctaState.basicLabelKey) : ''}
                premiumCtaLabel={ctaState.premiumLabelKey ? t(ctaState.premiumLabelKey) : ''}
                onBasicClick={handleLandingBasicCta}
                onPremiumClick={handleLandingPremiumCta}
                basicDisabled={ctaState.basicDisabled}
                premiumDisabled={ctaState.premiumDisabled}
                hideBasicCta={ctaState.hideBasicCta}
              />
            )}
          </AnimatedText>

          <AnimatedText delay={240} duration={700}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', marginTop: '56px' }}>
            <button
              onClick={() => { trackEvent('cta_hero_clicked', { position: 'section_features', label: 'Analyser mes trades gratuitement' }); window.location.href = '/register'; }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: 'clamp(14px, 3.5vw, 16px) clamp(20px, 7vw, 40px)', borderRadius: '100px', background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', color: '#fff', fontSize: 'clamp(0.82rem, 4vw, 1rem)', fontWeight: 700, border: 'none', cursor: 'pointer', letterSpacing: '-0.02em', boxShadow: '0 0 60px rgba(139,92,246,0.3)' }}
            >
              {t('landing.common.cta_analyze_free')}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.78rem', margin: 0 }}>{t('landing.common.instant_access')}</p>
          </div>
          </AnimatedText>
        </div>
      </section>



      {/* ── Atlas AI Section ── */}
      {(() => {
        // Les textes vivent dans `landing.atlas.s1..s4` ; on ne garde ici que la
        // structure (severites, couleur du plan) qui pilote le rendu.
        const scenario = (id, patterns, planPreview) => ({
          label: t(`landing.atlas.${id}.label`),
          userMsg: t(`landing.atlas.${id}.user`),
          intro: t(`landing.atlas.${id}.intro`),
          patterns: patterns.map((severity, index) => ({
            severity,
            title: t(`landing.atlas.${id}.p${index + 1}_title`),
            costLabel: t(`landing.atlas.${id}.p${index + 1}_cost`),
            body: t(`landing.atlas.${id}.p${index + 1}_body`),
            tip: t(`landing.atlas.${id}.p${index + 1}_tip`),
          })),
          planPreview,
          outro: t(`landing.atlas.${id}.outro`),
        });

        const scenarios = [
          scenario('s1', ['critical', 'high', 'high']),
          scenario('s2', ['good', 'good', 'medium']),
          scenario('s3', ['critical', 'high', 'medium']),
          scenario('s4', [], {
            name: t('landing.atlas.s4.plan_name'),
            color: '#E9D5FF',
            conditions: [1, 2, 3, 4, 5, 6].map(i => t(`landing.atlas.s4.c${i}`)),
          }),
        ];

        const [activeIdx, setActiveIdx] = React.useState(0);
        const [displayedPatterns, setDisplayedPatterns] = React.useState<number>(0);
        const [showIntro, setShowIntro] = React.useState(false);
        const [showOutro, setShowOutro] = React.useState(false);
        const [animKey, setAnimKey] = React.useState(0);
        const [planConfirmState, setPlanConfirmState] = React.useState<'idle'|'processing'|'done'>('idle');

        React.useEffect(() => {
          setDisplayedPatterns(0);
          setShowIntro(false);
          setShowOutro(false);
          setPlanConfirmState('idle');
          const k = animKey;
          const t1 = setTimeout(() => { if (animKey === k) setShowIntro(true); }, 200);
          const t2 = setTimeout(() => { if (animKey === k) setDisplayedPatterns(1); }, 800);
          const t3 = setTimeout(() => { if (animKey === k) setDisplayedPatterns(2); }, 1250);
          const t4 = setTimeout(() => { if (animKey === k) setDisplayedPatterns(3); }, 1700);
          const t6 = setTimeout(() => { if (animKey === k) setDisplayedPatterns(4); }, 2150);
          const t7 = setTimeout(() => { if (animKey === k) setDisplayedPatterns(5); }, 2450);
          const t8 = setTimeout(() => { if (animKey === k) setDisplayedPatterns(6); }, 2750);
          const t5 = setTimeout(() => { if (animKey === k) setShowOutro(true); }, 3100);
          const t9 = setTimeout(() => { if (animKey === k) setPlanConfirmState('processing'); }, 4400);
          const t10 = setTimeout(() => { if (animKey === k) setPlanConfirmState('done'); }, 5600);
          return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); clearTimeout(t5); clearTimeout(t6); clearTimeout(t7); clearTimeout(t8); clearTimeout(t9); clearTimeout(t10); };
        }, [animKey]);

        const handleSelect = (i: number) => {
          setActiveIdx(i);
          setAnimKey(k => k + 1);
        };

        const s = scenarios[activeIdx];

        const severityColor: Record<string, string> = {
          critical: '#f87171',
          high: '#fb923c',
          medium: '#facc15',
          good: '#4ade80',
        };

        const parseInline = (text: string) => {
          return text
            .replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight:700;color:#fff">$1</strong>');
        };

        return (
          <section style={{ background: '#0B0B0C', padding: '100px 0', position: 'relative', marginTop: '-1px' }}>

            <style>{`@media (max-width: 640px) { .atlas-pills-wrap { padding: 0 4px; } .atlas-pill { flex: 1 1 calc(50% - 4px); justify-content: center; display: flex !important; text-align: center; box-sizing: border-box; } }`}</style>

            <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>

              <AnimatedText delay={0} duration={700}>
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: '64px' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
                  <img src={atlasMainWebP} alt="Atlas" style={{ width: 18, height: 18, objectFit: 'contain', borderRadius: 4 }} />
                  <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', margin: 0 }}>{t('landing.atlas.eyebrow')}</p>
                </div>
                <h2 style={{ color: '#FFFFFF', fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 20px' }}>
                  {t('landing.atlas.title')}{' '}<span style={{ background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{t('landing.atlas.title_gradient')}</span>
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '1rem', maxWidth: '500px', lineHeight: 1.7, margin: '0 auto' }}>
                  {t('landing.atlas.subtitle')}
                </p>
              </div>
              </AnimatedText>

              <AnimatedText delay={90} duration={700}>
              {/* Scenario tabs */}
              <div className="atlas-pills-wrap" style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '40px' }}>
                {scenarios.map((sc, i) => (
                  <button
                    key={i}
                    onClick={() => handleSelect(i)}
                    className="atlas-pill"
                    style={{
                      padding: '8px 16px',
                      borderRadius: '100px',
                      border: 'none',
                      background: activeIdx === i ? '#ffffff' : 'rgba(255,255,255,0.07)',
                      color: activeIdx === i ? '#0b0b0c' : 'rgba(255,255,255,0.5)',
                      fontSize: '0.82rem',
                      fontWeight: activeIdx === i ? 600 : 400,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {sc.label}
                  </button>
                ))}
              </div>

              {/* Chat window */}
              <div style={{ background: 'rgba(255,255,255,0.02)', border: 'none', borderRadius: '24px', overflow: 'hidden' }}>

                {/* Title bar */}
                <div style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <img src={atlasMainWebP} alt="Atlas" style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 8 }} />
                  <span style={{ color: '#fff', fontSize: '1.05rem', fontWeight: 700, letterSpacing: '-0.01em' }}>Atlas</span>
                </div>

                {/* Messages */}
                <div style={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: '24px', minHeight: '520px' }}>

                  {/* User message */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ maxWidth: '75%' }}>
                      <div style={{ background: '#1e1e26', borderRadius: '18px', borderTopRightRadius: '4px', padding: '10px 16px' }}>
                        <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.875rem', lineHeight: 1.6 }}>{s.userMsg}</span>
                      </div>
                      <div style={{ textAlign: 'right', marginTop: '4px', paddingRight: '4px' }}>
                        <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.25)' }}>23:31</span>
                      </div>
                    </div>
                    <img src={hugoFxLogoWebP} alt="User" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, marginTop: 2 }} />
                  </div>

                  {/* Atlas response */}
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <img src={atlasMainWebP} alt="Atlas" style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 6, flexShrink: 0, marginTop: 2 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>

                      {/* Intro text */}
                      <div style={{ opacity: showIntro ? 1 : 0, transform: showIntro ? 'translateY(0)' : 'translateY(6px)', transition: 'opacity 0.4s ease, transform 0.4s ease', marginBottom: '12px' }}>
                        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.875rem', lineHeight: 1.7, margin: 0 }}
                          dangerouslySetInnerHTML={{ __html: parseInline(s.intro) }} />
                      </div>

                      {/* Pattern cards */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
                        {s.patterns.map((p, pi) => (
                          <div key={pi} style={{
                            opacity: displayedPatterns > pi ? 1 : 0,
                            transform: displayedPatterns > pi ? 'translateY(0)' : 'translateY(8px)',
                            transition: 'opacity 0.45s ease, transform 0.45s ease',
                            background: 'rgba(255,255,255,0.04)',
                            borderRadius: '12px',
                            padding: '14px 16px',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '6px' }}>
                              {p.severity === 'good' ? (
                                <svg style={{ width: 18, height: 18, flexShrink: 0, marginTop: 1 }} viewBox="0 0 24 24" fill="none">
                                  <path fillRule="evenodd" clipRule="evenodd" d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM16.0303 8.96967C16.3232 9.26256 16.3232 9.73744 16.0303 10.0303L11.0303 15.0303C10.7374 15.3232 10.2626 15.3232 9.96967 15.0303L7.96967 13.0303C7.67678 12.7374 7.67678 12.2626 7.96967 11.9697C8.26256 11.6768 8.73744 11.6768 9.03033 11.9697L10.5 13.4393L12.7348 11.2045L14.9697 8.96967C15.2626 8.67678 15.7374 8.67678 16.0303 8.96967Z" fill={severityColor[p.severity] ?? '#4ade80'} />
                                </svg>
                              ) : (
                                <svg style={{ width: 18, height: 18, flexShrink: 0, marginTop: 1 }} viewBox="0 0 24 24" fill="none">
                                  <path fillRule="evenodd" clipRule="evenodd" d="M7.84308 3.80211C9.8718 2.6007 10.8862 2 12 2C13.1138 2 14.1282 2.6007 16.1569 3.80211L16.8431 4.20846C18.8718 5.40987 19.8862 6.01057 20.4431 7C21 7.98943 21 9.19084 21 11.5937V12.4063C21 14.8092 21 16.0106 20.4431 17C19.8862 17.9894 18.8718 18.5901 16.8431 19.7915L16.1569 20.1979C14.1282 21.3993 13.1138 22 12 22C10.8862 22 9.8718 21.3993 7.84308 20.1979L7.15692 19.7915C5.1282 18.5901 4.11384 17.9894 3.55692 17C3 16.0106 3 14.8092 3 12.4063V11.5937C3 9.19084 3 7.98943 3.55692 7C4.11384 6.01057 5.1282 5.40987 7.15692 4.20846L7.84308 3.80211ZM13 16C13 16.5523 12.5523 17 12 17C11.4477 17 11 16.5523 11 16C11 15.4477 11.4477 15 12 15C12.5523 15 13 15.4477 13 16ZM12 6.25C12.4142 6.25 12.75 6.58579 12.75 7V13C12.75 13.4142 12.4142 13.75 12 13.75C11.5858 13.75 11.25 13.4142 11.25 13V7C11.25 6.58579 11.5858 6.25 12 6.25Z" fill={severityColor[p.severity] ?? '#fb923c'} />
                                </svg>
                              )}
                              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#fff', lineHeight: 1.3 }}>{p.title}</span>
                            </div>
                            {p.costLabel && (
                              <p style={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(248,113,113,0.9)', margin: '0 0 6px 28px' }}>{p.costLabel}</p>
                            )}
                            {p.body && (
                              <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, margin: '0 0 6px 28px' }}>{p.body}</p>
                            )}
                            {p.tip && (
                              <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.8)', fontWeight: 600, margin: '4px 0 0 28px' }}>{p.tip}</p>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Plan preview card (for plan scenario) */}
                      {s.planPreview && (
                        <div style={{
                          opacity: displayedPatterns >= 1 ? 1 : 0,
                          transform: displayedPatterns >= 1 ? 'translateY(0)' : 'translateY(8px)',
                          transition: 'opacity 0.45s ease, transform 0.45s ease',
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.07)',
                          borderRadius: '14px',
                          padding: '16px',
                          marginBottom: '12px',
                        }}>
                          {/* Plan name + color chip */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                            <div style={{ width: 14, height: 14, borderRadius: '50%', background: s.planPreview.color, flexShrink: 0 }} />
                            <span style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 700 }}>{s.planPreview.name}</span>
                          </div>
                          {/* Conditions list */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                            {s.planPreview.conditions.map((cond: string, ci: number) => (
                              <div key={ci} style={{
                                opacity: displayedPatterns > ci ? 1 : 0,
                                transform: displayedPatterns > ci ? 'translateY(0)' : 'translateY(6px)',
                                transition: `opacity 0.35s ease ${ci * 0.1}s, transform 0.35s ease ${ci * 0.1}s`,
                                display: 'flex', alignItems: 'flex-start', gap: '8px',
                              }}>
                                <div style={{ width: 6, height: 6, borderRadius: '50%', background: s.planPreview.color, marginTop: 6, flexShrink: 0 }} />
                                <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{cond}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Outro */}
                      <div style={{ opacity: showOutro ? 1 : 0, transform: showOutro ? 'translateY(0)' : 'translateY(6px)', transition: 'opacity 0.4s ease, transform 0.4s ease' }}>
                        <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, margin: 0 }}
                          dangerouslySetInnerHTML={{ __html: parseInline(s.outro) }} />
                      </div>

                      {/* Confirmation buttons (for plan scenario) */}
                      {s.planPreview && showOutro && (
                        <div style={{ opacity: showOutro ? 1 : 0, transform: showOutro ? 'translateY(0)' : 'translateY(6px)', transition: 'opacity 0.4s ease 0.3s, transform 0.4s ease 0.3s', marginTop: '14px' }}>
                          {planConfirmState === 'done' ? (
                            <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)', textAlign: 'right' }}>{t('landing.atlas.confirmed')}</p>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                              {/* Refuser — exact bento-button-secondary dark */}
                              <button style={{
                                padding: '6px 12px',
                                borderRadius: '14px',
                                border: 'none',
                                background: '#0a0a0d',
                                color: 'rgba(255,255,255,0.8)',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                cursor: 'default',
                                opacity: planConfirmState === 'processing' ? 0.4 : 1,
                                transition: 'opacity 0.2s',
                              }}>
                                {t('landing.atlas.refuse')}
                              </button>
                              {/* Accepter — exact bento-button gradient */}
                              <button style={{
                                padding: '6px 12px',
                                borderRadius: '14px',
                                border: 'none',
                                background: 'linear-gradient(135deg, #3d2a6e 0%, #7C76FF 40%, #c45aaa 70%, #FF7AD9 100%)',
                                color: '#fff',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                cursor: 'default',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'opacity 0.2s',
                              }}>
                                {planConfirmState === 'processing' ? (
                                  <>
                                    <svg style={{ width: 10, height: 10, animation: 'spin 1s linear infinite' }} viewBox="0 0 24 24" fill="none">
                                      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3"/>
                                      <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                                    </svg>
                                    <span>{t('landing.atlas.processing')}</span>
                                  </>
                                ) : (
                                  <span>{t('landing.atlas.accept')}</span>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                    </div>
                  </div>

                </div>

                {/* Input bar — copie exacte du vrai Atlas */}
                <div style={{ padding: '12px 16px 16px' }}>
                  <div style={{ position: 'relative', width: '100%', maxWidth: '520px', margin: '0 auto' }}>
                    <div style={{
                      backgroundColor: '#2a2a31',
                      borderRadius: '9999px',
                      padding: '10px 52px 10px 20px',
                      minHeight: '44px',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.875rem', lineHeight: 1.5, userSelect: 'none' }}>{t('landing.atlas.input_placeholder')}</span>
                    </div>
                    {/* Send button — exact bento-button */}
                    <div style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3d2a6e 0%, #7C76FF 40%, #c45aaa 70%, #FF7AD9 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M12 20L12 4M12 4L18 10M12 4L6 10" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>

                </div>
              </div>
              </AnimatedText>

            </div>

            <AnimatedText delay={160} duration={700}>
            {/* CTA */}
            <div style={{ marginTop: '64px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
              <button
                onClick={() => { trackEvent('cta_hero_clicked', { position: 'section_atlas', label: 'Essaie Atlas gratuitement' }); window.location.href = '/register'; }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '16px 40px', borderRadius: '100px', background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', color: '#fff', fontSize: '1rem', fontWeight: 700, border: 'none', cursor: 'pointer', letterSpacing: '-0.02em', boxShadow: '0 0 60px rgba(139,92,246,0.3)' }}
              >
                {t('landing.atlas.cta')}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.78rem', margin: 0 }}>{t('landing.common.instant_access')}</p>
            </div>
            </AnimatedText>

          </section>
        );
      })()}

      {/* ── Backtest Section ── */}
      <section style={{ background: '#101114', padding: '100px 0 120px', position: 'relative', overflow: 'hidden', marginTop: '-1px' }}>
        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '1px', height: '1px', boxShadow: '0 0 520px 180px rgba(139,92,246,0.07)', pointerEvents: 'none' }} />
        <style>{`
          .bt-block{display:flex;align-items:center;gap:64px;padding:0 48px}
          .bt-block>div{flex:1 1 0;min-width:0}
          .bt-block--rev{flex-direction:row-reverse}
          @media(max-width:900px){.bt-block,.bt-block--rev{flex-direction:column!important;gap:32px;padding:0 20px}}
          @keyframes btFloat0{0%,100%{transform:translateX(0) rotate(-1deg)}50%{transform:translateX(6px) rotate(-1deg)}}
          @keyframes btFloat1{0%,100%{transform:translateX(0) rotate(0.5deg)}50%{transform:translateX(-5px) rotate(0.5deg)}}
          @keyframes btFloat2{0%,100%{transform:translateX(0) rotate(-0.8deg)}50%{transform:translateX(7px) rotate(-0.8deg)}}
          @keyframes btFloat3{0%,100%{transform:translateX(0) rotate(1deg)}50%{transform:translateX(-4px) rotate(1deg)}}
        `}</style>

        <div style={{ maxWidth: '1180px', margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 }}>

          {/* Header */}
          <AnimatedText delay={0} duration={700}>
            <div style={{ textAlign: 'center', marginBottom: '80px' }}>
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 20px' }}>{t('landing.backtest.eyebrow')}</p>
              <h2 style={{ color: '#FFFFFF', fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 20px' }}>
                {t('landing.backtest.title')}{' '}
                <span style={{ background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{t('landing.backtest.title_gradient')}</span>
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '1rem', maxWidth: '520px', margin: '0 auto', lineHeight: 1.7 }}>
                {t('landing.backtest.subtitle')}
              </p>
            </div>
          </AnimatedText>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '96px' }}>

            {/* ── Backtest demo chart interactif ── */}
            <AnimatedText delay={40} duration={700}>
              <BacktestDemoChart />
            </AnimatedText>

            {/* ── Bloc 1 : Trade cards GAUCHE · Copy + Bullets DROITE ── */}
            <AnimatedText delay={60} duration={700}>
              <div className="bt-block">
                {/* Cards staggerées — copie exacte du style TradeItem */}
                <style>{`
                  @media (max-width: 900px) {
                    .bt-trade-card { margin-left: 0 !important; margin-right: 0 !important; }
                  }
                `}</style>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {([
                    { pair: 'EUR/USD', dir: 'LONG' as const,  date: '14 Jan 2025 · 09:32', lots: '0.10', pnl: '+284.50', session: t('landing.backtest.trade_card.london'),   ml: '0%',  mr: '14%' },
                    { pair: 'GBP/USD', dir: 'SHORT' as const, date: '15 Jan 2025 · 14:07', lots: '0.05', pnl: '-120.00', session: t('landing.backtest.trade_card.new_york'),  ml: '18%', mr: '0%'  },
                    { pair: 'XAU/USD', dir: 'LONG' as const,  date: '16 Jan 2025 · 10:45', lots: '0.02', pnl: '+518.25', session: t('landing.backtest.trade_card.london'),   ml: '6%',  mr: '10%' },
                    { pair: 'NAS100',  dir: 'SHORT' as const, date: '17 Jan 2025 · 15:22', lots: '0.01', pnl: '+339.80', session: t('landing.backtest.trade_card.new_york'),  ml: '12%', mr: '2%'  },
                  ]).map((trade, i) => {
                    const isLong = trade.dir === 'LONG';
                    const isWin = !trade.pnl.startsWith('-');
                    return (
                      <div key={i} className="bt-trade-card" style={{
                        marginLeft: trade.ml, marginRight: trade.mr,
                        background: '#0a0a0d', borderRadius: '8px', overflow: 'hidden',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)',
                        display: 'flex', alignItems: 'stretch',
                        animation: `btFloat${i} ${6 + i * 1.3}s ease-in-out infinite`,
                      }}>
                        <div style={{ width: 4, flexShrink: 0, background: isLong ? 'linear-gradient(to bottom, #10b981, #059669)' : 'linear-gradient(to bottom, #f43f5e, #be123c)' }} />
                        <div style={{ flex: 1, padding: '12px 16px 12px 14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ position: 'relative', width: 40, height: 40, borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isLong ? 'rgba(16,185,129,0.05)' : 'rgba(244,63,94,0.05)' }}>
                            {isLong
                              ? <TrendingUp style={{ width: 20, height: 20, color: '#10b981' }} />
                              : <TrendingDown style={{ width: 20, height: 20, color: '#f43f5e' }} />
                            }
                            <div style={{ position: 'absolute', top: -2, right: -2, width: 8, height: 8, borderRadius: '50%', background: isWin ? '#10b981' : '#f59e0b' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap' as const, alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                              <span style={{ padding: '2px 8px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 500, background: 'rgba(107,114,128,0.1)', color: '#fff' }}>{trade.session}</span>
                              <span style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 700 }}>{trade.pair}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>
                              <span>{trade.date}</span>
                              <span>·</span>
                              <span style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>{trade.lots} {t('landing.backtest.trade_card.lot')}</span>
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <p style={{ color: isWin ? '#10b981' : '#f43f5e', fontSize: '1rem', fontWeight: 700, margin: 0 }}>{trade.pnl} USD</p>
                            <p style={{ color: isWin ? 'rgba(16,185,129,0.6)' : 'rgba(244,63,94,0.6)', fontSize: '0.7rem', margin: '2px 0 0' }}>{t('landing.backtest.trade_card.net_pnl')}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Copy + bullets */}
                <div>
                  <p style={{ color: 'rgba(167,139,250,0.8)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', margin: '0 0 16px' }}>{t('landing.backtest.journal.eyebrow')}</p>
                  <h3 style={{ color: '#FFFFFF', fontSize: 'clamp(1.4rem, 2.2vw, 2rem)', fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 16px', lineHeight: 1.15 }}>
                    {t('landing.backtest.journal.title')}
                  </h3>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.95rem', lineHeight: 1.7, margin: '0 0 28px' }}>
                    {t('landing.backtest.journal.subtitle')}
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {[
                      'b1', 'b2', 'b3',
                    ].map((b, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', fontWeight: 500, lineHeight: 1.5 }}>
                        <CheckCircle2 style={{ width: 18, height: 18, flexShrink: 0, color: '#8B5CF6', marginTop: 2 }} />
                        {t(`landing.backtest.journal.${b}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </AnimatedText>

            {/* ── Bloc 2 : Stats dashboard GAUCHE · Copy DROITE ── */}
            <AnimatedText delay={120} duration={700}>
              <div className="bt-block">
                {/* Copy LEFT */}
                <div>
                  <p style={{ color: 'rgba(167,139,250,0.8)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', margin: '0 0 16px' }}>{t('landing.backtest.simulation.eyebrow')}</p>
                  <h3 style={{ color: '#FFFFFF', fontSize: 'clamp(1.4rem, 2.2vw, 2rem)', fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 16px', lineHeight: 1.15 }}>
                    {t('landing.backtest.simulation.title_1')}<br/>{t('landing.backtest.simulation.title_2')}
                  </h3>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                    {t('landing.backtest.simulation.body_1')}<br/>{t('landing.backtest.simulation.body_2')}<br/>{t('landing.backtest.simulation.body_3')}
                  </p>
                </div>

                {/* Stats cards */}
                <div>
                  <style>{`
                    #bt-stats-grid .grid { grid-template-columns: repeat(2, 1fr) !important; gap: 14px !important; }
                    #bt-stats-grid .grid > div:nth-child(1) { transform: translateX(-28px); animation: btFloat0 7s ease-in-out infinite; }
                    #bt-stats-grid .grid > div:nth-child(2) { transform: translateX(-28px); animation: btFloat1 8.3s ease-in-out infinite; }
                    #bt-stats-grid .grid > div:nth-child(3) { transform: translateX(28px); animation: btFloat2 6.8s ease-in-out infinite; }
                    #bt-stats-grid .grid > div:nth-child(4) { transform: translateX(28px); animation: btFloat3 9.1s ease-in-out infinite; }
                    #bt-stats-grid .bento-card { font-size: 0.8em; background-color: #0a0a0d !important; border: none !important; box-shadow: 0 4px 24px rgba(0,0,0,0.5) !important; color: #fff !important; }
                    #bt-stats-grid .text-2xl { font-size: 1.25rem !important; color: #ffffff !important; }
                    #bt-stats-grid .text-xl { font-size: 1rem !important; color: #ffffff !important; }
                    #bt-stats-grid p.text-sm, #bt-stats-grid span.text-sm { font-size: 0.68rem !important; }
                    #bt-stats-grid .text-xs { font-size: 0.65rem !important; }
                    #bt-stats-grid .min-w-\\[250px\\] { min-width: 0 !important; }
                    #bt-stats-grid .text-content-secondary { color: rgba(255,255,255,0.45) !important; }
                    #bt-stats-grid .text-content-primary { color: #ffffff !important; }
                    #bt-stats-grid .font-semibold, #bt-stats-grid .font-bold { color: #ffffff !important; }
                    #bt-stats-grid .bg-background-secondary\\/30 { background-color: #0a0a0d !important; }
                    #bt-stats-grid .stroke-background-secondary\\/30 { stroke: #0a0a0d !important; }
                    #bt-stats-grid .cursor-help,
                    #bt-stats-grid button[data-break-even-settings] { display: none !important; }
                    @media (max-width: 900px) {
                      #bt-stats-grid { zoom: 0.82; }
                      #bt-stats-grid .grid > div:nth-child(1),
                      #bt-stats-grid .grid > div:nth-child(2),
                      #bt-stats-grid .grid > div:nth-child(3),
                      #bt-stats-grid .grid > div:nth-child(4) { transform: none !important; }
                    }
                    @media (max-width: 640px) {
                      #bt-stats-grid { zoom: 0.68; }
                    }
                    @media (max-width: 420px) {
                      #bt-stats-grid { zoom: 0.58; }
                    }
                  `}</style>
                  <div id="bt-stats-grid" className="dark">
                    <AccountStatsCards
                      currency="USD"
                      displayFormat="currency"
                      initialBalance={100000}
                      stats={{
                        totalTrades: 66,
                        winningTradesCount: 44,
                        losingTradesCount: 21,
                        breakEvenTradesCount: 1,
                        winRate: 66.67,
                        totalProfit: 11571.38,
                        totalReturnPercentage: 11.57,
                        currentBalance: 111571.38,
                        maxBalance: 114200,
                        profitFactor: { profits: 45409.45, losses: -33825.95 },
                        averageRiskReward: 2.34,
                        maxDrawdown: 12.4,
                        averageTradeReturn: 175.32,
                        lastUpdated: new Date(),
                      }}
                    />
                  </div>
                </div>
              </div>
            </AnimatedText>

          </div>

          {/* CTA */}
          <AnimatedText delay={200} duration={700}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', marginTop: '80px' }}>
              <button
                onClick={() => { trackEvent('cta_hero_clicked', { position: 'section_backtest', label: 'Tester ma stratégie gratuitement' }); window.location.href = '/register'; }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: 'clamp(14px, 3.5vw, 16px) clamp(20px, 7vw, 40px)', borderRadius: '100px', background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', color: '#fff', fontSize: 'clamp(0.82rem, 4vw, 1rem)', fontWeight: 700, border: 'none', cursor: 'pointer', letterSpacing: '-0.02em', boxShadow: '0 0 60px rgba(139,92,246,0.3)' }}
              >
                {t('landing.backtest.cta')}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.78rem', margin: 0 }}>{t('landing.common.instant_access')}</p>
            </div>
          </AnimatedText>


        </div>
      </section>

{/* Main Features Section - Carousel */}
  <section className="pt-12 sm:pt-16 md:pt-20 lg:pt-24 pb-16 sm:pb-20 md:pb-24 lg:pb-32 relative overflow-hidden" style={{ background: '#0B0B0C', marginTop: '-1px' }}>
        {/* Fond animé avec gradients */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient orb 1 - Violet */}
          <div 
            className="absolute top-0 -left-20 w-96 h-96 rounded-full opacity-20 blur-3xl"
            style={{
              background: 'radial-gradient(circle, rgba(139, 92, 246, 0.22) 0%, rgba(139, 92, 246, 0) 70%)',
              animation: 'float 20s ease-in-out infinite'
            }}
          />
          {/* Gradient orb 2 - Rose/Orange */}
          <div 
            className="absolute top-40 -right-20 w-96 h-96 rounded-full opacity-20 blur-3xl"
            style={{
              background: 'radial-gradient(circle, rgba(236, 72, 153, 0.2) 0%, rgba(251, 146, 60, 0.18) 50%, rgba(236, 72, 153, 0) 70%)',
              animation: 'float 25s ease-in-out infinite reverse'
            }}
          />
          {/* Gradient orb 3 - Bleu */}
          <div 
            className="absolute bottom-20 left-1/3 w-96 h-96 rounded-full opacity-15 blur-3xl"
            style={{
              background: 'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0) 70%)',
              animation: 'float 30s ease-in-out infinite'
            }}
          />
          {/* Grille subtile */}
          <div 
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
              backgroundSize: '50px 50px'
            }}
          />
        </div>

        <div className="max-w-none mx-auto px-2 sm:px-8 lg:px-24 relative z-10">
          <div className="text-center max-w-6xl mx-auto">
            <AnimatedText delay={0} duration={700}>
              <div style={{ textAlign: 'center', marginBottom: '56px' }}>
                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 16px' }}>{t('landing.tools.eyebrow')}</p>
                <h2 style={{ color: '#ffffff', fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 16px' }}>
                  {t('landing.tools.title')}
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '1rem', maxWidth: '560px', margin: '0 auto', lineHeight: 1.7 }}>
                  {t('landing.tools.subtitle')}
                </p>
              </div>
            </AnimatedText>
          </div>

          <AnimatedText delay={280} duration={700}>
          {/* Carousel */}
          <FeaturesCarousel />
          </AnimatedText>

          <AnimatedText delay={360} duration={700}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', marginTop: '56px' }}>
            <button
              onClick={() => { trackEvent('cta_hero_clicked', { position: 'section_replay', label: 'Analyser mes trades gratuitement' }); window.location.href = '/register'; }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: 'clamp(14px, 3.5vw, 16px) clamp(20px, 7vw, 40px)', borderRadius: '100px', background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', color: '#fff', fontSize: 'clamp(0.82rem, 4vw, 1rem)', fontWeight: 700, border: 'none', cursor: 'pointer', letterSpacing: '-0.02em', boxShadow: '0 0 60px rgba(139,92,246,0.18)' }}
            >
              {t('landing.common.cta_analyze_free')}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.82rem', margin: 0 }}>{t('landing.common.instant_access')}</p>
          </div>
          </AnimatedText>
        </div>
      </section>



      {/* ── FAQ Section ── */}
      <section style={{ background: '#101114', padding: '100px 24px 80px', marginTop: '-1px' }} id="faq">
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>

          <AnimatedText delay={0} duration={700}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 20px' }}>{t('landing.faq.eyebrow')}</p>
            <h2 style={{ color: '#FFFFFF', fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', fontWeight: 800, letterSpacing: '-0.04em', margin: 0 }}>{t('landing.faq.title')}</h2>
          </div>
          </AnimatedText>

          <AnimatedText delay={90} duration={700}>
          {(() => {
            const [openFaq, setOpenFaq] = React.useState(null);
            const faqs = [1, 2, 3, 4, 5].map(i => ({
              q: t(`landing.faq.q${i}`),
              a: t(`landing.faq.a${i}`),
            }));
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {faqs.map((faq, i) => (
                  <div key={i} style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '14px', border: 'none', transition: 'all 0.2s', overflow: 'hidden' }}>
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '16px' }}
                    >
                      <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', fontWeight: 600 }}>{faq.q}</span>
                      <svg style={{ flexShrink: 0, transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M10 4V16M4 10H16" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </button>
                    {openFaq === i && (
                      <div style={{ padding: '0 24px 20px' }}>
                        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.88rem', lineHeight: 1.7, margin: 0 }}>{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
          </AnimatedText>

          <AnimatedText delay={160} duration={700}>
          <div style={{ textAlign: 'center', marginTop: '56px' }}>
            <button
              onClick={() => { trackEvent('cta_hero_clicked', { position: 'section_final', label: 'Commencer gratuitement' }); window.location.href = '/register'; }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '16px 40px', borderRadius: '100px', background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', color: '#fff', fontSize: '1rem', fontWeight: 700, border: 'none', cursor: 'pointer', letterSpacing: '-0.02em', boxShadow: '0 0 60px rgba(139,92,246,0.3)' }}
            >
              {t('landing.common.cta_start_free')}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
          </AnimatedText>

        </div>
      </section>


      {/* Footer */}
      <Footer showSeparator={false} backgroundColor="#0B0B0C" isDark={true} />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
